/**
 * REGISTRO de callables de la 2.0. Añadir una función = añadir una línea aquí.
 * Fase 0: solo la de salud, que sirve de patrón. Cada fase añade las suyas en su dominio.
 */
import { z } from 'zod';
import { defineCall } from './define.js';
import { ListingInputSchema } from '../listings/schema.js';
import {
  ImportSourceSchema,
  MAX_IMPORT_ITEMS,
  MAX_IMPORT_URLS,
  type ImportedItem,
  type ExternalReputation,
} from '../listings/import.js';
import {
  DISPUTE_REASONS,
  FulfillmentModeSchema,
  type OFFER_STATUSES,
  type OrderStatus,
} from '../firestore/orders.js';
import {
  CountryCodeSchema,
  LanguageCodeSchema,
  MoneySchema,
  type Currency,
  type Money,
} from '../firestore/primitives.js';
import { MAX_BUNDLE_ITEMS, type BundleQuote, type CheckoutQuote } from '../orders/checkout.js';
import { ProTierSchema, type ProTier } from '../firestore/users.js';

// ---------- admin ----------

export const HealthInputSchema = z.object({ echo: z.string().max(100).optional() });
export interface HealthOutput {
  env: 'sandbox' | 'prod';
  version: string;
  node: string;
  echo?: string;
}

// ---------- users ----------

/** Resultado de `users-geo` (HTTP GET /api/geo, no callable) y base de la decisión 13b en el front. */
export interface GeoOutput {
  country: string | null;
  /** `header` = x-country-code de Hosting; `override` = cabecera de prueba (solo sandbox); `none` = desconocido. */
  source: 'header' | 'override' | 'none';
  listed: boolean;
  signup: boolean;
  selling: boolean;
  buying: boolean;
}

/**
 * users-signupGuard (13b): se llama ANTES de crear el perfil. Comprueba país de la IP (si Hosting lo
 * da), prefijo del teléfono y país declarado contra `settings/countries`. Lanza
 * `failed-precondition` con `details.reason` si algo no cuadra.
 */
export const SignupGuardInputSchema = z.object({
  /** Teléfono en E.164 si el registro lleva OTP. */
  phone_e164: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/)
    .optional(),
  /** País declarado por el usuario (dirección o selector). */
  country_code: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
});
export type SignupGuardReason =
  | 'ip_country_not_available'
  | 'phone_prefix_not_allowed'
  | 'country_not_available'
  | 'country_mismatch';
export interface SignupGuardOutput {
  ok: true;
  /** País con el que queda el registro (declarado > teléfono > IP). */
  country: string | null;
  ip_country: string | null;
}

/**
 * users-onSignup: tras CUALQUIER inicio de sesión. Si `users/{uid}` no existe pasa el guard 13b y
 * crea el perfil con los campos de servidor (rol, verificaciones, contadores a 0). Si ya existe,
 * no vuelve a comprobar la IP (cuenta verificada de viaje, 13b) y solo refresca `last_seen_at`.
 */
export const OnSignupInputSchema = z.object({
  phone_e164: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/)
    .optional(),
  country_code: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
  language: z
    .string()
    .regex(/^[a-z]{2}$/)
    .optional(),
  full_name: z.string().max(120).optional(),
  /** A3: uid del que invita (enlace ?ref=); se fija UNA vez al crear el perfil. */
  ref: z.string().min(6).max(128).optional(),
});
export interface OnSignupOutput {
  created: boolean;
  country: string | null;
  role: 'user' | 'staff' | 'admin';
}

/** users-waitlist: "avísame cuando lleguéis a mi país" desde la página de no disponible. */
export const WaitlistInputSchema = z.object({
  email: z.string().email().max(254),
  country_code: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
  language: z
    .string()
    .regex(/^[a-z]{2}$/)
    .optional(),
});
export interface WaitlistOutput {
  ok: true;
}

/**
 * users-getPublicProfile: perfil PÚBLICO de un vendedor (ficha, perfil `/:lang/u/:handle`), sin
 * sesión. Decisión D16 (13/10): en público solo el @usuario; nunca nombre real, correo ni teléfono.
 * Se busca por `handle` (username) o por `uid`.
 */
export const PublicProfileInputSchema = z
  .object({
    uid: z.string().min(1).max(128).optional(),
    handle: z.string().min(1).max(60).optional(),
  })
  .refine((v) => (v.uid === undefined) !== (v.handle === undefined), {
    message: 'indica uid o handle (uno de los dos)',
  });
export interface PublicTraderInfo {
  legal_name: string;
  trade_name: string | null;
  address: string;
  contact_email: string;
  contact_phone: string | null;
  vat_number: string | null;
  registration_number: string | null;
  returns_terms: string | null;
  status: 'pending' | 'verified' | 'rejected' | 'suspended';
}
/** Escaparate PRO (1.0 Storefront.jsx): portada, descripción, galería y anuncios fijados arriba. */
export interface PublicStorefront {
  cover_url: string | null;
  description: string;
  photos: string[];
  pinned: string[];
}
export const STOREFRONT_MAX_PHOTOS = 8;
export const STOREFRONT_MAX_PINNED = 6;
export const SaveStorefrontInputSchema = z.object({
  cover_url: z.string().url().max(2000).nullable(),
  description: z.string().trim().max(1000),
  photos: z.array(z.string().url().max(2000)).max(STOREFRONT_MAX_PHOTOS),
  pinned: z.array(z.string().min(1).max(128)).max(STOREFRONT_MAX_PINNED),
});
/** Buscador de vendedores (1.0 searchSellers): por @usuario, desde la misma caja de búsqueda. */
export const SearchSellersInputSchema = z.object({
  query: z.string().trim().max(60),
  limit: z.number().int().min(1).max(8).optional(),
});
export interface SellerCard {
  uid: string;
  handle: string;
  avatar_url: string | null;
  rating_avg: number;
  rating_count: number;
  total_sales: number;
  is_pro: boolean;
  country_code: string | null;
}
export interface SearchSellersOutput {
  sellers: SellerCard[];
}
/** Fotos de anuncio en el servidor (1.0 editListingPhoto / cleanListingPhotoBackground). */
export const EditPhotoInputSchema = z.object({
  url: z.string().url().max(2000),
  rotate: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]),
});
export const PhotoUrlInputSchema = z.object({ url: z.string().url().max(2000) });
/** Estudio de fotos con IA (21/10, Gabor): solo planes con `ai_studio_per_month` > 0 (PRO Absoluto). */
export const AI_STUDIO_MODES = ['smooth', 'mannequin'] as const;
export const AiStudioInputSchema = z.object({
  url: z.string().url().max(2000),
  /** smooth = quitar arrugas; mannequin = la prenda en un maniquí sobre fondo blanco. */
  mode: z.enum(AI_STUDIO_MODES),
});
export interface PhotoOutput {
  url: string;
  /** Quitar fondo: usos que le quedan este mes (PRO). */
  remaining?: number;
}
export interface PhotoToolsOutput {
  is_pro: boolean;
  background_enabled: boolean;
  remaining: number;
  limit: number;
  /** Fotos con IA al mes que incluye el plan (0 = no lo incluye) y las que quedan este mes. */
  ai_studio_limit: number;
  ai_studio_remaining: number;
}
/** Tarjetas guardadas (1.0 SavedCardsManager): las del cliente de Stripe del usuario. */
export interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}
export interface ListCardsOutput {
  cards: SavedCard[];
}
export const RemoveCardInputSchema = z.object({ id: z.string().min(1).max(128) });

export interface PublicProfileOutput {
  uid: string;
  /** Particular o profesional (DSA art. 30); `trader` solo cuando es profesional. */
  seller_type: 'private' | 'trader';
  trader: PublicTraderInfo | null;
  /** Nombre público: `username` o, si no tiene, `user-xxxxxx` (sufijo del uid). Nunca el nombre real. */
  handle: string;
  avatar_url: string | null;
  bio: string;
  country_code: string | null;
  city: string | null;
  is_pro: boolean;
  kyc_verified: boolean;
  email_verified: boolean;
  phone_verified: boolean;
  total_sales: number;
  seller_level: 'new' | 'reliable' | 'top' | 'elite';
  rating_avg: number;
  rating_count: number;
  /** ISO 8601 (1.0 `created_date` o 2.0 `created_at`). */
  member_since: string | null;
  vacation_mode: boolean;
  /** Escaparate (solo vendedores PRO que lo han rellenado). */
  storefront: PublicStorefront | null;
  /** A2: sellos verificados de otras plataformas («320 ventas y 4,9 en Vinted»), solo mientras no haya historial propio. */
  external_reputation: {
    source: ExternalReputation['source'];
    handle: string;
    sales_count: number | null;
    reviews_count: number | null;
    rating: number | null;
    profile_url: string;
  }[];
}

/**
 * import-extract (A1 «múdate en un clic»): perfil público de Vinted / Jófogás / Wallapop o una lista
 * de enlaces de anuncios → importJobs/{id} en estado `review` con los anuncios leídos.
 */
export const ImportExtractInputSchema = z
  .object({
    url: z.string().url().max(2000).optional(),
    urls: z.array(z.string().url().max(2000)).min(1).max(MAX_IMPORT_URLS).optional(),
    language: z.string().regex(/^[a-z]{2}$/),
  })
  .refine((v) => v.url !== undefined || v.urls !== undefined, 'url_or_urls');
/**
 * import-extractPages (D18, lector en navegador): páginas que el navegador del usuario ya descargó
 * de su plataforma (HTML de cada anuncio o JSON del armario de Vinted) → mismo `importJobs/{id}`
 * en `review`; con `job_id` se añaden a un job abierto (el lector envía por tandas).
 */
export const MAX_READER_PAGES = 40;
export const MAX_READER_PAGE_BYTES = 450_000;
export const ImportExtractPagesInputSchema = z
  .object({
    job_id: z.string().max(128).optional(),
    source: z.enum(['vinted', 'wallapop', 'jofogas', 'other']),
    profile_url: z.string().url().max(2000),
    language: z.string().regex(/^[a-z]{2}$/),
    pages: z
      .array(
        z
          .object({
            url: z.string().url().max(2000),
            html: z.string().max(MAX_READER_PAGE_BYTES).optional(),
            json: z.string().max(MAX_READER_PAGE_BYTES).optional(),
          })
          .refine((p) => p.html !== undefined || p.json !== undefined, 'html_or_json'),
      )
      .max(MAX_READER_PAGES),
    /** Última tanda: el job queda listo para revisar (puede ir sin páginas, solo para cerrar). */
    done: z.boolean(),
  })
  .refine((v) => v.pages.length > 0 || v.done, 'pages_or_done');
export interface ImportExtractOutput {
  job_id: string;
  source: ExternalReputation['source'];
  items: ImportedItem[];
  /** Enlaces que no se pudieron leer (y por qué), para enseñarlos. */
  skipped: { url: string; reason: string }[];
}
/** import-publish: publica en segundo plano los anuncios seleccionados del job (progreso en el doc). */
export const ImportPublishInputSchema = z.object({
  job_id: z.string().min(1).max(64),
  selected: z.array(z.string().min(1).max(120)).min(1).max(MAX_IMPORT_ITEMS),
  /** Fotos ya subidas por el navegador (marcador) a uploads/{uid}, por external_id. */
  photo_overrides: z
    .record(z.string().min(1).max(120), z.array(z.string().url().max(2000)).max(50))
    .optional(),
});
export interface ImportPublishOutput {
  job_id: string;
  queued: number;
}
/** import-linkReputation (A2): enlaza un perfil externo; devuelve las cifras y el código a poner en la bio. */
export const LinkReputationInputSchema = z.object({
  url: z.string().url().max(2000),
});
export interface LinkReputationOutput {
  source: ExternalReputation['source'];
  handle: string;
  sales_count: number | null;
  reviews_count: number | null;
  rating: number | null;
  verification_code: string;
  verified: boolean;
}
export const UnlinkReputationInputSchema = z.object({ source: ImportSourceSchema });

/**
 * users-setUsername (D17): el @usuario es obligatorio y único; se elige en el alta y las cuentas
 * migradas sin él lo eligen al primer login. Reserva en `usernames/{normalizado}` (transacción).
 */
export const SetUsernameInputSchema = z.object({
  username: z.string().trim().min(3).max(21),
});
export interface SetUsernameOutput {
  username: string;
  username_normalized: string;
}

/**
 * admin-grantRole: cambia el rol de una cuenta (custom claim `admin`/`staff` + `users.role`) con
 * auditLog. Solo admins; nadie puede quitarse el admin a sí mismo. El PRIMER admin del sandbox se
 * pone con `pnpm admin:grant sandbox <email>` (tools/seed), porque aún no hay nadie que llame a esto.
 */
export const GrantRoleInputSchema = z
  .object({
    uid: z.string().min(1).max(128).optional(),
    email: z.string().email().optional(),
    role: z.enum(['user', 'staff', 'admin']),
    reason: z.string().max(500).optional(),
  })
  .refine((v) => (v.uid === undefined) !== (v.email === undefined), {
    message: 'indica uid o email (uno de los dos)',
  });
export interface GrantRoleOutput {
  ok: true;
  uid: string;
  role: 'user' | 'staff' | 'admin';
  previous_role: string | null;
}

// ---------- ai (sección 11) ----------

/**
 * ai-translateListing (D1, tabla 11 #2): título y descripción de un anuncio en el idioma del
 * visitante. Una traducción por anuncio e idioma: se guarda en `products.translations[lang]`
 * (la usa también el SEO S3) y la siguiente petición no llama al modelo. Sin sesión (la ficha es
 * pública); interruptor `ai.listing_translation`.
 */
export const TranslateListingInputSchema = z.object({
  product_id: z.string().min(1).max(128),
  target: z.string().regex(/^[a-z]{2}$/),
});
export interface TranslateListingOutput {
  title: string;
  description: string;
  /** Idioma de origen declarado del anuncio. */
  source: string;
  /** `cached`: ya estaba en el anuncio; `fresh`: la acaba de hacer el modelo. */
  from: 'cached' | 'fresh' | 'same_language';
}

/**
 * ai-translateQuery (1.0 `queryVariants` con gtx): lo tecleado en el buscador, en los idiomas con
 * catálogo, para que "pantalón" encuentre "nadrág". Caché por consulta en `aiCache`; interruptor
 * `ai.search_query_translation`.
 */
export const TranslateQueryInputSchema = z.object({
  q: z.string().min(2).max(120),
  /** Idioma del que escribe (para no traducir a él). */
  source: z.string().regex(/^[a-z]{2}$/),
});
export interface TranslateQueryOutput {
  /** Una variante por idioma destino (sin la original, que el cliente ya tiene). */
  variants: string[];
  from: 'cached' | 'fresh' | 'disabled';
}

// ---------- listings (fase 1: publicación) ----------

/**
 * listings-create (1.0 `createSellerListing` + `moderateProductListing`): valida, completa (vendedor,
 * `search_tokens`, `category_path`, +18) y modera (términos prohibidos → rechazado; si no, activo
 * o `pending_review` según el interruptor `listing_human_review`). `draft` guarda sin publicar.
 */
export const CreateListingInputSchema = z.object({
  listing: ListingInputSchema,
  mode: z.enum(['publish', 'draft']).default('publish'),
});
export interface CreateListingOutput {
  id: string;
  status: 'active' | 'pending_review' | 'draft' | 'rejected';
  /** Motivo de rechazo (términos prohibidos encontrados) o de revisión. */
  moderation_reason: string | null;
  is_adult: boolean;
}

/** listings-update: el dueño edita; lo que cambia vuelve a pasar por la moderación. */
export const UpdateListingInputSchema = z.object({
  product_id: z.string().min(1).max(128),
  listing: ListingInputSchema,
  mode: z.enum(['publish', 'draft']).default('publish'),
});

/** listings-setAutopilot (D2): el dueño configura el piloto automático de un anuncio. */
export const SetAutopilotInputSchema = z.object({
  product_id: z.string().min(1).max(128),
  price_drop: z
    .object({
      percent: z.number().int().min(1).max(50),
      every_days: z.number().int().min(1).max(30),
      min_price: z.number().positive(),
    })
    .nullable(),
  auto_accept_min_price: z.number().positive().nullable(),
});
export interface SetAutopilotOutput {
  ok: true;
  next_drop_at: string | null;
}
/** listings-sellerStats (D3): panel del vendedor. */
export const SellerStatsInputSchema = z.object({});
export interface SellerStatsRow {
  product_id: string;
  title: string;
  status: string;
  price: number;
  currency: string;
  photo_url: string | null;
  views: number;
  favorites: number;
  offers: number;
  pending_offers: number;
  days_listed: number;
  /** Consejo (clave i18n de `profile.myListings.tip*`) o null. */
  tip: 'tipLowerPrice' | 'tipBetterPhoto' | 'tipOffers' | null;
  autopilot: boolean;
}
export interface SellerStatsOutput {
  rows: SellerStatsRow[];
  totals: {
    active: number;
    sold: number;
    views: number;
    favorites: number;
    offers: number;
    /** Ventas completadas (pedidos) de los últimos 30 días y su importe en la moneda del vendedor. */
    sold_30d: number;
    revenue_30d: Money | null;
  };
}
/** listings-recordProductView (1.0 productEngagement.recordProductView): visita única por anuncio. */
export const RecordProductViewInputSchema = z.object({
  product_id: z.string().min(1).max(128),
  /** UUID v4 del visitante anónimo (localStorage); ignorado si hay sesión. */
  visitor_id: z.string().max(64).optional(),
});
export interface RecordProductViewOutput {
  views: number;
  favorites_count: number;
  counted: boolean;
}
/** listings-setStatus: pausar / reactivar / marcar vendido fuera de nuvvu / borrar (dueño). */
export const SetListingStatusInputSchema = z.object({
  product_id: z.string().min(1).max(128),
  status: z.enum(['active', 'paused', 'sold', 'deleted']),
  /**
   * Solo con `sold` (1.0 MarkSoldModal, Gabor 20/10): a quién se vendió, de entre quienes han
   * chateado por el anuncio. Sin él = vendido fuera de nuvvu.
   */
  buyer_id: z.string().min(1).max(128).optional(),
});
export interface SetListingStatusOutput {
  status: 'active' | 'paused' | 'sold' | 'deleted';
}

/**
 * ai-listingFromPhotos (tabla 11 #1, "publicar en 60 s"): a partir de las fotos ya subidas, el
 * modelo propone título, descripción (en el idioma del vendedor), categoría (id del árbol v2),
 * marca, estado, color, talla y palabras clave. El vendedor corrige y publica.
 */
export const ListingFromPhotosInputSchema = z.object({
  photo_urls: z.array(z.string().url()).min(1).max(4),
  language: z.string().regex(/^[a-z]{2}$/),
  /** Pista opcional del vendedor ("vaqueros Levi's talla 32"). */
  hint: z.string().max(200).optional(),
});
export interface ListingFromPhotosOutput {
  title: string;
  description: string;
  category_id: string | null;
  brand: string;
  condition: 'new' | 'like_new' | 'good' | 'fair' | 'poor' | null;
  color: string;
  size: string;
  search_keywords: string;
  /** Precio sugerido en EUR (unidades), null si no se atreve. */
  price_hint_eur: number | null;
}

// ---------- marketplace (fase 2: checkout con precio total, A7) ----------

/** Elección de envío del comprador (lo que viaja del cliente al servidor). */
export const ShippingPickSchema = z.object({
  carrier_id: z.string().min(1).max(64),
  service_code: z.string().min(1).max(80),
  pickup_point: z
    .object({
      id: z.string().min(1).max(80),
      name: z.string().max(200),
      address: z.string().max(300),
    })
    .nullable()
    .optional(),
});
/** Una opción de envío con el precio ya en la moneda del comprador. */
export interface CheckoutShippingOption {
  carrier_id: string;
  service_code: string;
  service_level: 'locker' | 'pickup_point' | 'home';
  name: string;
  carrier_name: string;
  price: Money;
  requires_pickup_point: boolean;
  external_carrier_id: string | null;
  estimated_days: number | null;
}
/**
 * marketplace-quoteCheckout: desglose del pedido ANTES de pagar (precio del anuncio en la moneda del
 * vendedor → cobro en la del comprador al tipo del día, protección, envío estimado o tarifa «en
 * mano», recargo). Es `quoteCheckout` de shared con los datos del servidor; placeOrder recalcula.
 */
export const CheckoutQuoteInputSchema = z.object({
  product_id: z.string().min(1).max(128),
  fulfillment_mode: FulfillmentModeSchema,
  /** Oferta aceptada que se va a pagar (el precio sale de la oferta, no del anuncio). */
  offer_id: z.string().min(1).max(128).optional(),
  /** Lote (D5): el resto de artículos del MISMO vendedor (product_id es el primero). */
  product_ids: z
    .array(z.string().min(1).max(128))
    .max(MAX_BUNDLE_ITEMS - 1)
    .optional(),
  /** País de destino (de la dirección elegida); sin él, el del comprador. */
  country_code: CountryCodeSchema.optional(),
  /** Código postal de destino (cotización más precisa de los transportistas). */
  postal_code: z.string().max(20).optional(),
  /** C1: pagar primero con el saldo del wallet (misma moneda que el comprador); la tarjeta cubre el resto. */
  use_wallet: z.boolean().optional(),
  /** Sección 8: transportista + servicio elegidos (y punto de recogida si el servicio lo exige). */
  shipping: ShippingPickSchema.optional(),
  /** L4: protección opcional (artículos por encima del umbral). */
  optional_protection: z.boolean().optional(),
});
/** C1: qué parte cubre el wallet y qué parte la tarjeta (moneda del comprador). */
export interface CheckoutWalletSplit {
  /** Saldo disponible del wallet del comprador. */
  available: Money;
  /** Parte del total que se retiene en el wallet (0 si `use_wallet` no venía). */
  wallet_portion: Money;
  /** Resto a pagar con tarjeta (0 = sin pasar por Stripe). */
  card_portion: Money;
}
export interface CheckoutQuoteOutput {
  quote: CheckoutQuote;
  /** Transportistas disponibles para la ruta (vacío = tabla de tramos + envío manual del vendedor). */
  shipping_options: CheckoutShippingOption[];
  /** La opción aplicada al presupuesto (la elegida, o la más barata si no se eligió). */
  shipping_selected: CheckoutShippingOption | null;
  /** null si el comprador no tiene wallet con saldo en su moneda. */
  wallet: CheckoutWalletSplit | null;
  /** Lote: subtotal, tramo aplicado y descuento (moneda del vendedor); null si es un artículo. */
  bundle: BundleQuote | null;
  items: { product_id: string; title: string; photo_url: string | null; price: Money }[];
  buyer_currency: Currency;
  seller_handle: string;
  /** DSA art. 30 / derechos del consumidor: aviso «compras a un particular / profesional». */
  seller_type: 'private' | 'trader';
  /** Minutos que durará la reserva al pulsar pagar. */
  reservation_minutes: number;
}

/**
 * marketplace-placeOrder: crea el pedido en `pending_payment`, reserva el artículo 30 min, crea el
 * pago (tramo tarjeta) y devuelve la URL de Stripe Checkout (autorización; captura al aceptar).
 */
export const PlaceOrderInputSchema = z.object({
  product_id: z.string().min(1).max(128),
  fulfillment_mode: FulfillmentModeSchema,
  offer_id: z.string().min(1).max(128).optional(),
  product_ids: z
    .array(z.string().min(1).max(128))
    .max(MAX_BUNDLE_ITEMS - 1)
    .optional(),
  /** `users/{uid}/addresses/{id}`; obligatoria en modo shipping. */
  address_id: z.string().min(1).max(128).optional(),
  /** Nota para quedar (handover), opcional. */
  handover_note: z.string().trim().max(500).optional(),
  /** C1: retener primero el saldo del wallet; si cubre todo, no se pasa por Stripe. */
  use_wallet: z.boolean().optional(),
  shipping: ShippingPickSchema.optional(),
  /** L4: protección opcional (artículos por encima del umbral). */
  optional_protection: z.boolean().optional(),
  /**
   * Total que el comprador tiene en pantalla (`CheckoutQuoteOutput.quote.amounts.total`). Si al colocar el pedido el
   * total real es otro (p. ej. la cotización del transportista cambió entre el desglose y el pago), el servidor
   * responde `failed-precondition` / `quote_changed` con el total nuevo en `details.total` y no cobra nada.
   */
  expected_total: MoneySchema.optional(),
  language: LanguageCodeSchema,
});
export interface PlaceOrderOutput {
  order_id: string;
  payment_id: string;
  /** Stripe Checkout, o la ficha del pedido si el wallet cubrió todo (C1). */
  checkout_url: string;
  /** Epoch ms en que caduca la reserva y la sesión de pago. */
  expires_at: number;
}

/**
 * payments-confirmOrderPayment: respaldo si el webhook tarda (1.0 confirmOrderPayment): consulta la
 * sesión de Stripe y, si está autorizada, aplica `payment_authorized`. Idempotente.
 */
export const ConfirmOrderPaymentInputSchema = z.object({
  order_id: z.string().min(1).max(128),
  session_id: z.string().max(200).optional(),
});
export interface ConfirmOrderPaymentOutput {
  status: OrderStatus;
  /** true si el pago está autorizado (ya o ahora). */
  paid: boolean;
}

/**
 * marketplace-respondToOrder: el vendedor acepta (se captura el pago y arranca el envío/quedada) o
 * rechaza (se anula la autorización: el dinero nunca sale) una venta en `paid_pending_acceptance`.
 */
export const RespondToOrderInputSchema = z.object({
  order_id: z.string().min(1).max(128),
  /** `cancel`: anular una venta ya aceptada (antes de enviarla), con reembolso íntegro. */
  action: z.enum(['accept', 'reject', 'cancel']),
  reason: z.string().trim().max(500).optional(),
});
export interface RespondToOrderOutput {
  status: OrderStatus;
}

/** shipping-markShipped: el vendedor marca el paquete como enviado (envío manual: nº de seguimiento opcional). */
// ---- payments: Stripe Connect, retiradas (D7) y recargas (plan 14) ----
export const ConnectOnboardingInputSchema = z.object({ language: z.string().min(2).max(5) });
export interface ConnectOnboardingOutput {
  /** Enlace de onboarding de Stripe (null cuando ya está todo activo). */
  url: string | null;
  status: 'none' | 'pending' | 'enabled';
  payout_currency: Currency | null;
}
export const WithdrawalMethodSchema = z.enum(['standard', 'instant']);
export const WithdrawalQuoteInputSchema = z.object({
  /** Importe en la unidad menor de la moneda del wallet. */
  amount: z.number().int().positive(),
  method: WithdrawalMethodSchema,
});
export interface WithdrawalQuoteOutput {
  amount: Money;
  payout: Money;
  payout_currency: Currency;
  fx_rate: number;
  fx_surcharge: Money;
  fx_percent: number;
  needs_fx_notice: boolean;
  min: Money;
  available: Money;
  /** C3: comisión del retiro instantáneo según el nivel de vendedor (0 = gratis). */
  instant_fee: Money;
  instant_fee_percent: number;
}
export const RequestWithdrawalInputSchema = WithdrawalQuoteInputSchema.extend({
  fx_notice_accepted: z.boolean(),
});
export interface RequestWithdrawalOutput {
  withdrawal_id: string;
  status: 'requested' | 'approved' | 'paid';
}
export const CreateTopupSessionInputSchema = z.object({
  amount: z.number().int().positive(),
  language: z.string().min(2).max(5),
});
export interface CreateTopupSessionOutput {
  checkout_url: string;
}
export const ConfirmTopupInputSchema = z.object({ session_id: z.string().min(1).max(200) });
export interface ConfirmTopupOutput {
  credited: boolean;
}
export const ReviewWithdrawalInputSchema = z.object({
  withdrawal_id: z.string().min(1).max(128),
  action: z.enum(['approve', 'reject']),
  reason: z.string().max(500).optional(),
});
export interface ReviewWithdrawalOutput {
  status: 'approved' | 'paid' | 'rejected' | 'failed';
}

// ---- referidos (A3, T16) ----
export const InviteFriendInputSchema = z.object({
  email: z.string().email(),
  language: z.string().min(2).max(5),
});
export interface InviteFriendOutput {
  sent: boolean;
  /** Enlace con ?ref= para compartir. */
  link: string;
}
export interface ReferralStatusOutput {
  link: string;
  invited_count: number;
  rewards_count: number;
  free_bumps: number;
  /** Este usuario fue invitado y aún no ha usado su primera compra sin tarifa. */
  first_purchase_free: boolean;
}

// ---- admin (fase 3): ajustes, usuarios, transportistas, reportes ----
export const UpdateSettingsInputSchema = z.object({
  doc: z.enum([
    'fees',
    'proPlans',
    'featuredPrices',
    'countries',
    'company',
    'featureFlags',
    'legal',
    'socialPublisher',
    'adServer',
    'environment',
    'announcement',
  ]),
  /** Documento completo (sin updated_at/updated_by: los pone el servidor). Se valida con su schema. */
  data: z.record(z.string(), z.unknown()),
  reason: z.string().max(500).optional(),
});
export interface UpdateSettingsOutput {
  ok: true;
}
export const AdminSearchUsersInputSchema = z.object({
  /** email, @usuario o uid. */
  q: z.string().trim().min(2).max(200),
});
export interface AdminUserRow {
  uid: string;
  email: string | null;
  username: string | null;
  full_name: string | null;
  role: string;
  is_pro: boolean;
  banned: boolean;
  country_code: string | null;
  created_at: number | null;
}
export interface AdminSearchUsersOutput {
  users: AdminUserRow[];
}
export const AdminUpdateUserInputSchema = z.object({
  uid: z.string().min(1).max(128),
  banned: z.boolean().optional(),
  /** Días de restricción de mensajería (0 = levantar). */
  messaging_restricted_days: z.number().int().min(0).max(365).optional(),
  /** PRO por admin: tier + días (null = retirar el PRO de admin). */
  pro: z
    .object({ tier: ProTierSchema, days: z.number().int().positive().max(3650) })
    .nullable()
    .optional(),
  vacation_mode: z.boolean().optional(),
  /** DSA art. 30: verificación del vendedor profesional (`private/traderProfile.status`). */
  trader_status: z.enum(['verified', 'rejected', 'suspended', 'pending']).optional(),
  reason: z.string().max(500).optional(),
});
export interface AdminUpdateUserOutput {
  ok: true;
}
export const AdminUpdateCarrierInputSchema = z.object({
  id: z.string().min(1).max(64),
  /** Documento completo del carrier (se valida con CarrierSchema; `audit` lo pone el servidor). */
  data: z.record(z.string(), z.unknown()),
  reason: z.string().max(500).optional(),
});
export const AdminUpdateShippingCampaignInputSchema = z.object({
  /** Vacío = crear. */
  id: z.string().min(1).max(64).optional(),
  /** Campaña completa (se valida con ShippingCampaignSchema; spent/orders_count los lleva el servidor). */
  data: z.record(z.string(), z.unknown()),
  reason: z.string().max(500).optional(),
});
export interface AdminUpdateShippingCampaignOutput {
  id: string;
}
/** users-proSellers (1.0 getProSellers / getElitePromoSellers): tira «Vendedores PRO» de la home. */
export interface ProSellersOutput {
  sellers: {
    uid: string;
    handle: string;
    avatar_url: string | null;
    tier: 'basic' | 'plus' | 'business' | 'elite';
  }[];
}
/** admin-enrichListing (1.0 adminEnrichListing / AdminListingAI): completar un anuncio con IA. */
export const AdminEnrichListingInputSchema = z.object({
  product_id: z.string().min(1).max(128),
  /** No tocar la descripción si ya tiene una (> 10 caracteres). */
  skip_if_description: z.boolean().optional(),
});
export interface AdminEnrichListingOutput {
  product_id: string;
  skipped: boolean;
  reason?: string;
  result?: {
    category_id: string | null;
    description: string;
    brand: string;
    size: string;
    color: string;
  };
}
/** users-reportDiagnostic / admin-diagnostics (1.0 clientDiagnostics: errores del cliente con migas). */
export const ReportDiagnosticInputSchema = z.object({
  kind: z.enum(['error', 'boundary', 'manual']),
  message: z.string().min(1).max(500),
  context: z.string().max(120).optional(),
  route: z.string().max(200).optional(),
  platform: z.string().max(20).optional(),
  stack: z.string().max(4000).optional(),
  user_agent: z.string().max(300).optional(),
  session_id: z.string().max(64).optional(),
  breadcrumbs: z
    .array(z.object({ t: z.number(), msg: z.string().max(200) }))
    .max(20)
    .optional(),
});
export const AdminDiagnosticsInputSchema = z.object({
  limit: z.number().int().min(1).max(200).optional(),
  kind: z.enum(['error', 'boundary', 'manual']).optional(),
  uid: z.string().max(128).optional(),
});
export interface DiagnosticRow {
  id: string;
  kind: string;
  message: string;
  context: string;
  route: string;
  platform: string;
  uid: string | null;
  session_id: string | null;
  stack: string | null;
  user_agent: string | null;
  breadcrumbs: { t: number; msg: string }[];
  created_ms: number | null;
}
export interface AdminDiagnosticsOutput {
  items: DiagnosticRow[];
}
/** Encuesta a vendedores (1.0 sellerSurvey.js): users-sellerSurveyPrompt / submitSellerSurvey; admin-*. */
export interface SellerSurveyPromptOutput {
  show: boolean;
  survey?: {
    id: string;
    question: string;
    question_image_url: string | null;
    options: { id: string; label: string }[];
  };
}
export const SubmitSellerSurveyInputSchema = z.object({
  survey_id: z.string().min(1).max(64),
  option_id: z.string().min(1).max(48),
});
export const AdminSaveSellerSurveyInputSchema = z.object({
  /** Vacío = crear. */
  id: z.string().min(1).max(64).optional(),
  question: z.string().trim().min(1).max(2000),
  question_image_url: z.string().trim().max(2000).optional(),
  audience: z.enum(['sellers', 'all']),
  /** Etiquetas; el id se deriva de la etiqueta (o se conserva si viene). */
  options: z
    .array(
      z.object({ id: z.string().max(48).optional(), label: z.string().trim().min(1).max(120) }),
    )
    .min(2)
    .max(12),
  countries: z.array(z.string().trim().length(2)).min(1).max(40),
  active: z.boolean(),
});
export interface AdminSaveSellerSurveyOutput {
  id: string;
}
export const AdminSellerSurveysInputSchema = z.object({
  survey_id: z.string().min(1).max(64).optional(),
});
export interface AdminSellerSurveysOutput {
  surveys: {
    id: string;
    question: string;
    audience: 'sellers' | 'all';
    countries: string[];
    active: boolean;
    options: { id: string; label: string }[];
    updated_at: number | null;
  }[];
  responses: {
    id: string;
    uid: string;
    country: string;
    option_id: string;
    option_label: string;
    created_at: number | null;
  }[];
  stats: Record<string, number>;
}
export const AdminDeleteSellerSurveyResponseInputSchema = z.object({
  response_id: z.string().min(1).max(200),
});
/**
 * admin-sendUserEmail / admin-sendBulkEmail (1.0 AdminOutreach). 27/10: varios destinatarios
 * elegidos por nombre/email/id, enlaces como botones y adjuntos (subidos antes a
 * `admin_mail/{adminUid}/…` en Storage; máx. 5 y 10 MB en total). Se escribe en español y cada
 * destinatario lo recibe en su idioma.
 */
export const AdminEmailLinkSchema = z.object({
  label: z.string().trim().min(1).max(80),
  url: z
    .string()
    .trim()
    .url()
    .max(2000)
    .refine((u) => u.startsWith('https://'), 'https_only'),
});
export const AdminEmailAttachmentSchema = z.object({
  name: z.string().trim().min(1).max(160),
  path: z.string().regex(/^admin_mail\/[A-Za-z0-9_-]{1,128}\/[^/]{1,220}$/),
});
const AdminEmailExtrasSchema = {
  links: z.array(AdminEmailLinkSchema).max(10).optional(),
  attachments: z.array(AdminEmailAttachmentSchema).max(5).optional(),
};
export const AdminSendUserEmailInputSchema = z
  .object({
    /** Un destinatario (compatibilidad) o varios en `uids`. */
    uid: z.string().min(1).max(128).optional(),
    uids: z.array(z.string().min(1).max(128)).max(500).optional(),
    subject: z.string().trim().min(1).max(200),
    message: z.string().trim().min(1).max(12_000),
    ...AdminEmailExtrasSchema,
  })
  .refine((v) => Boolean(v.uid) || (v.uids?.length ?? 0) > 0, 'recipients_required');
export interface AdminSendUserEmailOutput {
  ok: true;
  /** Dirección del primer destinatario (compatibilidad). */
  to: string;
  total: number;
  sent: number;
  failed: number;
  /** uids sin email o que no existen. */
  skipped: string[];
}
export const AdminSendBulkEmailInputSchema = z.object({
  /** ISO-2 o 'ALL'. */
  country: z.string().trim().min(2).max(3),
  subject: z.string().trim().min(1).max(200),
  message: z.string().trim().min(1).max(12_000),
  /** true (por defecto): solo a quien tiene «marketing» activado; false = aviso de servicio a todos. */
  respect_marketing: z.boolean().optional(),
  dry_run: z.boolean().optional(),
  ...AdminEmailExtrasSchema,
});
export interface AdminSendBulkEmailOutput {
  total: number;
  sent: number;
  failed: number;
  dry_run: boolean;
}
/** admin-systemInfo / admin-refreshFxRates (1.0 AdminSystem sin secretos: salud, tipos de cambio, entorno). */
export interface SystemInfoOutput {
  health: HealthOutput;
  fx: { rates: Record<string, number>; updated_at_ms: number | null };
  environment: {
    env: string | null;
    maintenance: boolean;
    min_app_version: { android: string; ios: string } | null;
  };
  feature_flags: Record<string, boolean>;
  counts: { users: number; products_active: number; orders_open: number };
}
export interface RefreshFxRatesOutput {
  rates: Record<string, number>;
  updated_at_ms: number;
}
/** users-recordVisit / admin-visitorStats (1.0 analytics.js: visitantes únicos por día y país). */
export const RecordVisitInputSchema = z.object({
  /**
   * UUID generado en el navegador (localStorage); nunca datos personales. Solo existe con analítica
   * aceptada (L11): sin él la visita cuenta como página vista, no como visitante único.
   */
  visitor_id: z.string().uuid().optional(),
  path: z.string().max(200),
  /** La app vuelve a primer plano sin cambiar de ruta: solo «último acceso», no visita. */
  heartbeat: z.boolean().optional(),
});
/** Rango de fechas del admin (ms); sin nada = últimos 30 días; `from: 0` = desde el principio. */
export const AdminRangeInputSchema = z.object({
  from: z.number().int().min(0).optional(),
  to: z.number().int().min(0).optional(),
});
export interface VisitorStatsOutput {
  unique_visitors_total: number;
  unique_visitors_today: number;
  unique_visitors_7d: number;
  unique_visitors_30d: number;
  page_views_total: number;
  page_views_today: number;
  registered_users: number;
  /** Últimos 14 días, el más reciente primero. */
  days: { date: string; unique_visitors: number; page_views: number }[];
  top_countries: { code: string; unique_visitors: number }[];
  /** Rango pedido (27/10). */
  range: {
    from: number;
    to: number;
    /** Suma de visitantes únicos de cada día (un mismo visitante cuenta una vez por día). */
    daily_unique_sum: number;
    page_views: number;
    /** Visitantes cuya primera visita cae en el rango (exacto). */
    new_visitors: number;
    /** Visitantes únicos diarios por país en el rango (desde el 27/10; antes no se guardaba por día). */
    countries: { code: string; unique_visitors: number }[];
    /** Nuevos visitantes por país en el rango (exacto). */
    new_by_country: { code: string; count: number }[];
    /** Usuarios registrados en el rango por ciudad del perfil (los visitantes anónimos no tienen ciudad). */
    signups_by_city: { country: string; city: string; count: number }[];
  };
}
/** admin-moderateReview (1.0 AdminReviews: ocultar/restaurar una valoración; recalcula la media). */
export const AdminModerateReviewInputSchema = z.object({
  review_id: z.string().min(1).max(128),
  hidden: z.boolean(),
  reason: z.string().max(500).optional(),
});
/** admin-saveAdBanner / admin-deleteAdBanner (publicidad, 1.0 AdBanner). */
export const AdminSaveAdBannerInputSchema = z.object({
  /** Vacío = crear. */
  id: z.string().min(1).max(64).optional(),
  /** Anuncio completo (se valida con AdBannerSchema; impresiones/clics los lleva el servidor). */
  data: z.record(z.string(), z.unknown()),
  reason: z.string().max(500).optional(),
});
export interface AdminSaveAdBannerOutput {
  id: string;
}
export const AdminDeleteAdBannerInputSchema = z.object({
  id: z.string().min(1).max(64),
  reason: z.string().max(500).optional(),
});
/** marketplace-adEvent: impresión (una por sesión) o clic de un anuncio manual; público. */
export const AdEventInputSchema = z.object({
  ad_id: z.string().min(1).max(64),
  event: z.enum(['impression', 'click']),
});
/**
 * marketplace-featuredFeed (27/10): destacados «para ti», ordenados por el servidor
 * (packages/shared/src/featured/rank.ts) y paginados. Público. `seed`: aleatoria por visita y estable
 * al paginar; `categories`: raíces de lo visto recientemente (afinidad); `adult` solo lo pide la web
 * con sesión (las apps nunca). `country` null = toda Europa.
 */
export const FeaturedFeedInputSchema = z.object({
  seed: z.string().min(1).max(64),
  country: z.string().length(2).nullable().optional(),
  categories: z.array(z.string().max(64)).max(12).optional(),
  category: z.string().max(64).nullable().optional(),
  adult: z.boolean().optional(),
  offset: z.number().int().min(0).max(5000).optional(),
  limit: z.number().int().min(1).max(60).optional(),
});
export interface FeaturedFeedOutput {
  /** Ids de productos en orden; el cliente los lee de Firestore (caché y reglas de siempre). */
  ids: string[];
  /** Total que cumple los filtros (para «Ver los 3.000 destacados»). */
  total: number;
  /** Siguiente `offset`, o null si no hay más. */
  next: number | null;
}
/**
 * admin-startImpersonation (1.0 startImpersonation): un admin ve la app como el usuario. El token
 * lleva `impersonated_by` + `impersonation_session`: con él las reglas no dejan escribir y
 * `handle()` rechaza toda callable salvo users-endImpersonation (solo lectura, 1 h). Nunca a otro
 * admin ni a una cuenta desactivada. El admin conserva su propia sesión en el panel (otro origen).
 */
export const StartImpersonationInputSchema = z.object({
  uid: z.string().min(1).max(128),
  reason: z.string().max(500).optional(),
});
export interface StartImpersonationOutput {
  token: string;
  session_id: string;
  /** ms epoch. */
  expires_at: number;
  target: { username: string | null; email: string | null };
}
/** users-setSellerType / users-saveTraderProfile (DSA art. 30; derechos del consumidor). */
export const SetSellerTypeInputSchema = z.object({ seller_type: z.enum(['private', 'trader']) });
export const SaveTraderProfileInputSchema = z.object({
  legal_name: z.string().trim().min(2).max(200),
  trade_name: z.string().trim().max(120).optional(),
  tax_id: z.string().trim().min(3).max(40),
  vat_number: z.string().trim().max(40).optional(),
  registration_number: z.string().trim().max(60).optional(),
  address: z.object({
    line1: z.string().trim().min(3).max(200),
    postal_code: z.string().trim().min(2).max(20),
    city: z.string().trim().min(1).max(120),
    country_code: z.string().length(2),
  }),
  contact_email: z.string().email(),
  contact_phone: z.string().trim().max(30).optional(),
  returns_terms: z.string().trim().max(2000).optional(),
  declaration: z.literal(true),
});
/** ai-disputeAssistant (sección 11 #10; admin): resumen de la incidencia y propuesta de resolución. */
export const DisputeAssistantInputSchema = z.object({ dispute_id: z.string().min(1).max(128) });
export interface DisputeAssistantOutput {
  summary: string;
  /** Qué dice cada parte y qué muestran las fotos. */
  buyer_claim: string;
  seller_position: string;
  photos_assessment: string;
  recommendation: 'refund_full' | 'refund_partial' | 'release' | 'need_more_info';
  recommended_refund_percent: number | null;
  rationale: string;
  cached: boolean;
}
/** users-saveTaxProfile (DAC7, sección 15): datos fiscales del vendedor que supera el umbral. */
export const SaveTaxProfileInputSchema = z.object({
  legal_name: z.string().trim().min(2).max(200),
  tax_id: z.string().trim().min(3).max(40),
  tax_id_country: z.string().length(2),
  vat_number: z.string().trim().max(40).optional(),
  birth_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  registration_number: z.string().trim().max(60).optional(),
  address: z.object({
    line1: z.string().trim().min(3).max(200),
    postal_code: z.string().trim().min(2).max(20),
    city: z.string().trim().min(1).max(120),
    country_code: z.string().length(2),
  }),
});
export interface TaxProfileStatusOutput {
  status: 'missing' | 'requested' | 'provided' | 'blocked';
  deadline: number | null;
  sales_this_year: number;
  amount_eur_this_year: number;
}
/** users-requestDataExport / requestAccountDeletion / cancelAccountDeletion (GDPR, sección 15). */
export interface DataExportOutput {
  status: 'ready' | 'recent';
  url: string | null;
  expires_at: number | null;
  next_allowed_at: number | null;
}
export const RequestAccountDeletionInputSchema = z.object({
  reason: z.string().trim().max(1000).optional(),
});
export interface AccountDeletionOutput {
  scheduled_for: number;
}
/** marketplace-sendWithdrawalNotice: desistimiento (14 días) de una compra a un profesional. */
export const SendWithdrawalNoticeInputSchema = z.object({
  order_id: z.string().min(1).max(128),
  consumer_name_address: z.string().trim().min(3).max(300),
});
export interface SendWithdrawalNoticeOutput {
  notice_id: string;
  deadline: number;
}
/** admin-metrics (sección 10b «métricas que dicen si funciona»; ventana de 90 días). */
export interface AdminMetricsOutput {
  computed_at: number;
  window_days: number;
  /** Usuarios que compran Y venden en la ventana (objetivo 25 %) sobre usuarios con actividad. */
  buy_and_sell_pct: number;
  active_users: number;
  /** Compras pagadas (total o en parte) con saldo (objetivo 30 %). */
  wallet_paid_pct: number;
  orders_completed: number;
  /** Armarios importados en los últimos 7 días. */
  imports_last_7d: number;
  /** Repetición a 30 días: compradores de hace 30–60 días que volvieron a comprar en los últimos 30. */
  repeat_30d_pct: number;
  /** Tiempo medio de resolución de incidencias (horas) y abiertas ahora. */
  dispute_resolution_hours: number | null;
  disputes_open: number;
  /** Ventas cross-border sobre el total. */
  cross_border_pct: number;
  /** Anuncios publicados en los últimos 7 días y de ellos con IA (`ai_generated`). */
  listings_last_7d: number;
  listings_ai_pct: number;
  /** Rango pedido (27/10); las de arriba se calculan sobre él (los nombres «7d/90» son históricos). */
  range: { from: number; to: number };
  orders_created: number;
  orders_cancelled: number;
  /** Cancelados / creados, %. */
  cancel_pct: number;
  /** Volumen de los completados, EUR céntimos. */
  gmv_eur: number;
  avg_order_eur: number;
  buyers: number;
  sellers: number;
  new_users: number;
  listings_sold: number;
  /** Destino de los pedidos completados (top 8). */
  top_dest_countries: { code: string; orders: number }[];
  /** Horas medias de pago → aceptación y de aceptación → entrega. */
  avg_accept_hours: number | null;
  avg_delivery_hours: number | null;
}
/** Cumplimiento (sección 15): denuncia de anuncio/usuario, aviso DSA de contenido ilegal, colas del admin. */
export const ReportContentInputSchema = z.object({
  target: z.discriminatedUnion('type', [
    z.object({ type: z.literal('product'), product_id: z.string().min(1).max(128) }),
    z.object({ type: z.literal('user'), uid: z.string().min(1).max(128) }),
  ]),
  reason: z.enum([
    'prohibited',
    'counterfeit',
    'scam',
    'offensive',
    'spam',
    'wrong_category',
    'harassment',
    'other',
  ]),
  note: z.string().trim().max(1000).optional(),
});
export interface ReportContentOutput {
  report_id: string;
  already: boolean;
}
export const DsaNoticeInputSchema = z.object({
  location: z.string().url().max(500),
  explanation: z.string().min(20).max(4000),
  name: z.string().max(200).optional(),
  email: z.string().email().optional(),
  good_faith: z.literal(true),
  language: z.string().min(2).max(5),
  product_id: z.string().max(128).optional(),
  uid: z.string().max(128).optional(),
});
export interface DsaNoticeOutput {
  case_id: string;
  ref: string;
}
export const RequestModerationReviewInputSchema = z.object({
  decision_id: z.string().min(1).max(128),
  text: z.string().min(10).max(2000),
});
export const AdminResolveComplianceCaseInputSchema = z.object({
  id: z.string().min(1).max(128),
  status: z.enum(['in_review', 'actioned', 'rejected', 'closed']),
  resolution_note: z.string().max(4000).optional(),
  /** Al accionar sobre un anuncio: retirarlo (declaración de motivos al vendedor) o no tocarlo. */
  remove_listing: z.boolean().optional(),
});
export const AdminCreateComplianceCaseInputSchema = z.object({
  kind: z.enum(['gpsr_authority_order', 'safety_gate_alert', 'dac7_report']),
  product_id: z.string().max(128).optional(),
  year: z.number().int().min(2024).max(2100).optional(),
  description: z.string().min(1).max(8000),
  reporter_name: z.string().max(200).optional(),
  reporter_email: z.string().email().optional(),
});
export const AdminDac7SummaryInputSchema = z.object({ year: z.number().int().min(2024).max(2100) });
export interface Dac7SellerRow {
  uid: string;
  username: string | null;
  country_code: string | null;
  sales: number;
  /** Suma de `seller_amount.item` por moneda, en céntimos. */
  amounts: { currency: string; amount: number }[];
  amount_eur: number;
  reportable: boolean;
  tax_profile_status: string;
}
export interface Dac7SummaryOutput {
  year: number;
  threshold_sales: number;
  threshold_eur: number;
  sellers: Dac7SellerRow[];
}
/** Social publisher (1.0 adminSocialTestConnection / adminSocialPublishNow). */
export const SocialPublishNowInputSchema = z.object({
  kind: z.enum(['listing', 'seller', 'category', 'fact']).optional(),
  product_id: z.string().max(128).optional(),
  dry_run: z.boolean().optional(),
});
export interface SocialPostPreview {
  kind: string;
  lang: string;
  caption: string;
  caption_by: 'claude' | 'template';
  image: string;
  link: string;
  product_id: string | null;
  seller_id: string | null;
}
export interface SocialPublishNowOutput {
  preview: SocialPostPreview;
  /** null en dry-run; en sandbox (sin credenciales) status = 'shadow'. */
  entry: {
    id: string;
    status: string;
    facebook: string | null;
    instagram: string | null;
    errors: string[];
  } | null;
}
export interface SocialTestConnectionOutput {
  configured: boolean;
  page: { id: string; name: string } | null;
  instagram: { id: string; username: string } | null;
  errors: string[];
}
/** Newsletter (1.0 adminPreviewNewsletter / adminSendNewsletter / adminResetNewsletterHistory). */
export const NewsletterTextsInputSchema = z.object({
  subject: z.string().max(140).optional(),
  title: z.string().min(1).max(140),
  intro: z.string().max(2000).optional(),
  outro: z.string().max(2000).optional(),
  cta: z.string().max(60).optional(),
});
export interface NewsletterItem {
  id: string;
  title: string;
  price: number;
  currency: string;
  photo: string | null;
  featured: boolean;
}
export interface NewsletterPreviewOutput {
  items: NewsletterItem[];
  featured_count: number;
  recent_count: number;
  already_sent: number;
  available: number;
  recipients: number;
  by_lang: Record<string, number>;
}
export interface NewsletterSendOutput {
  sent: number;
  failed: number;
  products: number;
  recipients: number;
  languages: number;
}
/**
 * Newsletter programada (27/10): el admin la escribe una vez en español y se envía sola a la hora
 * fijada (hora de Madrid), traducida al idioma de cada usuario. `newsletters/_schedule`.
 */
export const NewsletterScheduleInputSchema = NewsletterTextsInputSchema.extend({
  enabled: z.boolean(),
  frequency: z.enum(['daily', 'weekly', 'monthly']),
  /** 0 = domingo … 6 = sábado (semanal). */
  weekday: z.number().int().min(0).max(6),
  /** Día del mes 1–28 (mensual). */
  monthday: z.number().int().min(1).max(28),
  /** Hora de Madrid 0–23. */
  hour: z.number().int().min(0).max(23),
});
export type NewsletterSchedule = z.infer<typeof NewsletterScheduleInputSchema>;
/** Foro (1.0 forumCreateThread / forumCreatePost / forumModerate). */
export const ForumCreateThreadInputSchema = z.object({
  category_id: z.string().min(1).max(64),
  title: z.string().min(3).max(140),
  body: z.string().min(3).max(5000),
  language: z.string().min(2).max(5),
});
export const ForumCreatePostInputSchema = z.object({
  thread_id: z.string().min(1).max(128),
  body: z.string().min(2).max(5000),
  language: z.string().min(2).max(5),
});
export interface ForumCreatedOutput {
  id: string;
}
export const ForumModerateInputSchema = z.object({
  action: z.enum([
    'delete_thread',
    'delete_post',
    'lock_thread',
    'unlock_thread',
    'hide_thread',
    'unhide_thread',
    'ban_user',
    'unban_user',
  ]),
  id: z.string().min(1).max(128),
});
export const ForumTranslateInputSchema = z.object({
  kind: z.enum(['thread', 'post']),
  id: z.string().min(1).max(128),
  target: z.string().min(2).max(5),
});
export interface ForumTranslateOutput {
  title: string | null;
  body: string;
  cached: boolean;
}
export const EmptyInputSchema = z.object({});
export interface ForumSeedOutput {
  categories: number;
  threads_created: number;
  threads_skipped: number;
}
/** support-createTicket (D7): abre un ticket; el bot de primer nivel contesta si está activado. */
export const CreateTicketInputSchema = z.object({
  category: z.enum(['purchase', 'sale', 'payment', 'shipping', 'account', 'translation', 'other']),
  subject: z.string().min(3).max(160),
  message: z.string().min(10).max(4000),
  attachments: z.array(z.string().url()).max(5).optional(),
  order_id: z.string().max(128).optional(),
  /** Ruta de la web desde la que se abrió soporte (sin origen). */
  page_url: z.string().max(500).optional(),
  language: z.string().min(2).max(5),
});
export interface CreateTicketOutput {
  ticket_id: string;
  /** Respuesta del bot (null si el bot está apagado o ha escalado directamente). */
  bot_reply: string | null;
  escalated: boolean;
}
export const ReplyTicketInputSchema = z.object({
  ticket_id: z.string().min(1).max(128),
  message: z.string().min(1).max(4000),
  attachments: z.array(z.string().url()).max(5).optional(),
});
export interface ReplyTicketOutput {
  bot_reply: string | null;
  escalated: boolean;
}
export const TicketIdInputSchema = z.object({ ticket_id: z.string().min(1).max(128) });
export const AgentReplyInputSchema = z.object({
  ticket_id: z.string().min(1).max(128),
  message: z.string().max(4000).optional(),
  status: z.enum(['open', 'in_progress', 'waiting_user', 'resolved', 'closed']).optional(),
});
/** admin-moderateListing: cola de revisión humana (+18 y anuncios `pending_review`/`rejected`). */
export const AdminModerateListingInputSchema = z.object({
  product_id: z.string().min(1).max(128),
  action: z.enum(['approve', 'reject', 'adult', 'safe', 'clear_override']),
  /** Motivo (obligatorio al rechazar; va al vendedor como declaración de motivos DSA). */
  reason: z.string().max(500).optional(),
});
/** admin-notifyTermsUpdated: aviso de cambio de documentos legales a todos los usuarios activos. */
export const AdminNotifyTermsUpdatedInputSchema = z.object({
  /** Fecha desde la que se aplican (AAAA-MM-DD). */
  effective_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dry_run: z.boolean().optional(),
});
export interface AdminNotifyTermsUpdatedOutput {
  total: number;
  notified: number;
  dry_run: boolean;
}
/** admin-orderAction: cancelar y reembolsar, o liberar el pago al vendedor, fuera de una incidencia. */
export const AdminOrderActionInputSchema = z.object({
  order_id: z.string().min(1).max(128),
  action: z.enum(['cancel_refund', 'release']),
  reason: z.string().trim().min(3).max(500),
});
/** admin-revenueReport: ingresos de la plataforma en un rango (sin tocar dinero). */
export const AdminRevenueReportInputSchema = z.object({
  /** Milisegundos (inclusive / exclusivo). */
  from: z.number().int().min(0),
  to: z.number().int().min(0),
});
export interface AdminRevenueByCurrency {
  currency: string;
  orders: number;
  /** Suma del artículo (GMV) y de lo que cobra nuvvu, en céntimos de esa moneda. */
  gmv: number;
  protection_fees: number;
  surcharges: number;
  campaign_subsidies: number;
}
export interface AdminRevenueReportOutput {
  from: number;
  to: number;
  by_currency: AdminRevenueByCurrency[];
  monetization: { kind: string; source: string; count: number }[];
  subscriptions: { tier: string; source: string; count: number }[];
  withdrawals: { status: string; count: number; instant_fees: Money | null }[];
}
/**
 * admin-dashboard (27/10, Gabor): todo el Panel en una llamada. Contadores por agregación (count) y
 * ingresos convertidos a EUR con el tipo BCE del día. `days`: ventana de «últimos N días».
 */
export const AdminDashboardInputSchema = z.object({
  days: z.number().int().min(1).max(365).optional(),
});
export interface AdminDashboardRevenue {
  /** Pedidos completados en la ventana. */
  sales: number;
  /** Importe de los artículos vendidos (lo que se lleva el vendedor antes de comisiones), EUR céntimos. */
  gmv: number;
  /** Tarifa de protección al comprador («seguro»), EUR céntimos. */
  protection: number;
  /** Recargo de transporte que cobra nuvvu, EUR céntimos. */
  transport: number;
  /** Envío regalado por campañas (resta), EUR céntimos. */
  campaign_subsidies: number;
  /** Destacados, subidas y escaparates pagados (no los incluidos en PRO), EUR céntimos. */
  promotions: number;
  promotions_count: number;
  /** Tarifas de retirada instantánea, EUR céntimos. */
  withdrawal_fees: number;
  /** protection + transport − subsidies + promotions + withdrawal_fees (PRO aparte: es recurrente). */
  total: number;
}
export interface AdminDashboardOutput {
  generated_at: number;
  days: number;
  listings: {
    active: number;
    pending_review: number;
    featured_paid: number;
    featured_paid_by_pack: { pack: string; count: number }[];
    featured_pro: number;
    spotlights: number;
  };
  users: {
    total: number;
    new_in_window: number;
    banned: number;
    blocks: number;
    pro: number;
    pro_by_tier: { tier: string; count: number }[];
    /** Ingreso PRO mensual recurrente (suscripciones de pago vivas × precio del plan), EUR céntimos. */
    pro_mrr: number;
  };
  orders: {
    last_24h: number;
    in_window: number;
    pending_acceptance: number;
    in_shipping: number;
    money_pending: number;
    disputes_open: number;
    withdrawals_requested: number;
  };
  support: {
    /** Tickets abiertos cuyo último mensaje es del usuario (esperan al equipo). */
    waiting_team: number;
    /** Escalados a una persona y sin cerrar (el bot ya no atiende). */
    escalated_open: number;
    reports_open: number;
    /** Conversaciones entre usuarios con mensajes en las últimas 24 h. */
    chats_24h: number;
  };
  revenue: { window: AdminDashboardRevenue; all_time: AdminDashboardRevenue };
}
export const AdminCloseReportInputSchema = z.object({
  id: z.string().min(1).max(128),
  status: z.enum(['resolved', 'dismissed']),
  note: z.string().max(1000).optional(),
});
export interface AdminOkOutput {
  ok: true;
}

// ---- PRO web por Stripe Billing (plan 14; 1.0 createProSubscriptionSession & co.) ----
export const CreateProSessionInputSchema = z.object({
  tier: ProTierSchema,
  language: z.string().min(2).max(5),
});
export interface CreateProSessionOutput {
  checkout_url: string;
}
export const ConfirmProInputSchema = z.object({ session_id: z.string().min(1).max(200) });
export interface ProStatusOutput {
  tier: ProTier | null;
  source: 'stripe' | 'app_store' | 'play_store' | 'admin_grant' | null;
  /** ms */
  expires_at: number | null;
  cancel_at_period_end: boolean;
  status: string | null;
}
export const ChangeProPlanInputSchema = z.object({ tier: ProTierSchema });

/**
 * Compras dentro de las apps nativas (F11): la app manda la prueba de compra de su tienda y el
 * servidor la verifica con la tienda antes de activar nada (nunca se fía del cliente).
 *  - App Store: `signed_transaction` = `Transaction.jwsRepresentation` de StoreKit 2 (JWS firmado por Apple).
 *  - Google Play: `purchase_token` de Play Billing + `product_id`.
 * Para destacados/bumps hacen falta `product_id` del anuncio (`listing_id`) y, en packs de un país, `country`.
 */
const IapExtrasSchema = {
  listing_id: z.string().min(1).max(128).optional(),
  country: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
  keywords: z.array(z.string().max(40)).max(10).optional(),
  language: z.string().regex(/^[a-z]{2}$/),
};
export const IapAppStoreInputSchema = z.object({
  signed_transaction: z.string().min(20).max(20_000),
  ...IapExtrasSchema,
});
export const IapPlayInputSchema = z.object({
  purchase_token: z.string().min(10).max(4000),
  store_product_id: z.string().min(3).max(80),
  ...IapExtrasSchema,
});
export interface IapOutput {
  /** Qué se activó: suscripción PRO o consumible; `already` si esa compra ya estaba aplicada. */
  kind: 'pro' | 'feature' | 'bump' | 'spotlight';
  activated: boolean;
  already: boolean;
  pro: ProStatusOutput | null;
}
export const ProCancelInputSchema = z.object({ action: z.enum(['cancel', 'reactivate']) });
export const BillingPortalInputSchema = z.object({ language: z.string().min(2).max(5) });
export interface BillingPortalOutput {
  url: string;
}

// ---- monetización: destacados, subidas y escaparate (1.0 createFeaturePackSession & co.) ----
export const MAX_FEATURE_KEYWORDS = 10;
export const PurchaseMonetizationInputSchema = z.object({
  kind: z.enum(['feature', 'bump', 'spotlight']),
  pack_code: z.string().min(1).max(40),
  /** feature y bump: anuncio propio. */
  product_id: z.string().min(1).max(128).optional(),
  /** feature con scope `one`. */
  country: CountryCodeSchema.optional(),
  /** feature, solo PRO. */
  keywords: z.array(z.string().trim().min(1).max(40)).max(MAX_FEATURE_KEYWORDS).optional(),
  method: z.enum(['wallet', 'card']),
  language: z.string().min(2).max(5),
});
export interface PurchaseMonetizationOutput {
  /** `card`: a Stripe; `wallet`: ya activado. */
  checkout_url: string | null;
  activated: boolean;
  charged: Money | null;
}
export const ConfirmMonetizationInputSchema = z.object({ session_id: z.string().min(1).max(200) });
export interface ConfirmMonetizationOutput {
  activated: boolean;
}
export const ActivateFreeSpotlightInputSchema = z.object({});
export interface ActivateFreeSpotlightOutput {
  days: number;
  /** ms */
  expires_at: number;
}

// ---- messaging (chat con traducción y guard antifraude, sección 11) ----
export const ConversationRefInputSchema = z.object({
  conversation_id: z.string().min(1).max(200),
});
export interface MarkReadOutput {
  ok: true;
}
export const ReportConversationInputSchema = ConversationRefInputSchema.extend({
  message_id: z.string().min(1).max(200).optional(),
  reason: z.enum(['impersonation', 'scam', 'abuse', 'other']),
  note: z.string().trim().max(1000).optional(),
});
export interface ReportConversationOutput {
  report_id: string;
}

// ---- notify (C8: push por FCM; sección 9, tokens por instalación) ----
/**
 * App Android «nuvvu Admin» (27/10): el móvil del admin registra su token FCM aquí (`adminDevices/{token}`,
 * aparte de los tokens de la app de usuario) para recibir los avisos de `functions/src/admin/alerts.ts`.
 */
export const AdminDeviceInputSchema = z.object({
  token: z.string().min(20).max(4096),
  platform: z.enum(['android', 'ios', 'web']).default('android'),
  app_version: z.string().max(40).optional(),
});
export interface AdminDeviceOutput {
  ok: true;
}
/** Envía un aviso de prueba (push a los dispositivos admin y correo a `settings/company.admin_alert_emails`). */
export const AdminTestAlertInputSchema = z.object({});
export interface AdminTestAlertOutput {
  ok: true;
  pushed: number;
  emailed: number;
}

export const RegisterPushTokenInputSchema = z.object({
  token: z.string().min(20).max(4096),
  platform: z.enum(['web', 'android', 'ios']),
  app_version: z.string().max(40).optional(),
});
export interface RegisterPushTokenOutput {
  ok: true;
}
export const UnregisterPushTokenInputSchema = z.object({
  token: z.string().min(20).max(4096),
});
export interface UnregisterPushTokenOutput {
  ok: true;
}

export const MarkShippedInputSchema = z.object({
  order_id: z.string().min(1).max(128),
  tracking_number: z.string().trim().max(80).optional(),
  carrier_name: z.string().trim().max(60).optional(),
});
export interface MarkShippedOutput {
  status: OrderStatus;
}

/** shipping-listPickupPoints: puntos/taquillas de un transportista cerca del comprador (Packeta hoy). */
export const ListPickupPointsInputSchema = z.object({
  carrier_id: z.string().min(1).max(64),
  country: CountryCodeSchema,
  postal_code: z.string().max(20).optional(),
  query: z.string().trim().max(80).optional(),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  /** Transportista externo dentro del agregador (p. ej. FoxPost en Packeta). */
  external_carrier_id: z.string().max(40).nullable().optional(),
  only_lockers: z.boolean().optional(),
  limit: z.number().int().min(1).max(30).optional(),
});
export interface PickupPointOutput {
  id: string;
  name: string;
  street: string;
  city: string;
  zip: string;
  country: string;
  lat: number | null;
  lng: number | null;
  opening: string;
  is_locker: boolean;
  distance_km: number | null;
}
export interface ListPickupPointsOutput {
  points: PickupPointOutput[];
}

/** marketplace-confirmDelivery: el comprador confirma que lo ha recibido (abre la ventana de 48 h). */
export const ConfirmDeliveryInputSchema = z.object({ order_id: z.string().min(1).max(128) });
export interface ConfirmDeliveryOutput {
  status: OrderStatus;
}

/** marketplace-confirmHandover (C7): el comprador teclea/escanea el código del vendedor → completado. */
export const ConfirmHandoverInputSchema = z.object({
  order_id: z.string().min(1).max(128),
  code: z.string().trim().min(1).max(20),
});
export interface ConfirmHandoverOutput {
  status: OrderStatus;
}

/** marketplace-openDispute (T6): incidencia del comprador en las 48 h tras la entrega; fotos obligatorias. */
export const OpenDisputeInputSchema = z.object({
  order_id: z.string().min(1).max(128),
  reason: z.enum(DISPUTE_REASONS),
  description: z.string().trim().min(10).max(4000),
  photo_urls: z.array(z.string().url()).min(1).max(10),
});
export interface OpenDisputeOutput {
  dispute_id: string;
  status: OrderStatus;
}

/** marketplace-resolveDispute (admin): reembolso íntegro, parcial (importe en moneda del comprador) o liberación. */
export const ResolveDisputeInputSchema = z.object({
  dispute_id: z.string().min(1).max(128),
  kind: z.enum(['refund_full', 'refund_partial', 'release']),
  refund_amount: MoneySchema.optional(),
  note: z.string().trim().max(2000).optional(),
});
export interface ResolveDisputeOutput {
  status: OrderStatus;
}

/** marketplace-submitReview (1.0 submitReview): valorar al otro tras un pedido completado; una por rol, permanente. */
export const SubmitReviewInputSchema = z.object({
  order_id: z.string().min(1).max(128),
  rating: z.number().int().min(1).max(5),
  text: z.string().trim().max(2000).optional(),
});
export interface SubmitReviewOutput {
  review_id: string;
}

/** marketplace-respondToDispute (vendedor, 72 h): devolver todo, proponer parcial o rechazar (mediación). */
export const RespondToDisputeInputSchema = z.object({
  dispute_id: z.string().min(1).max(128),
  kind: z.enum(['refund_full', 'refund_partial', 'reject', 'return']),
  /**
   * Solo refund_partial: parte del ARTÍCULO que se devuelve, en la moneda del comprador y como mucho
   * su precio (20/10). Con `include_shipping` se añaden los gastos de envío enteros (puede ir sin
   * artículo: solo el envío). Las tarifas de nuvvu nunca se devuelven en un parcial.
   */
  refund_amount: MoneySchema.optional(),
  include_shipping: z.boolean().optional(),
  /** Idioma para la vuelta de Stripe si el vendedor tiene que pagar la diferencia. */
  language: z
    .string()
    .regex(/^[a-z]{2}$/)
    .optional(),
  note: z.string().trim().max(2000).optional(),
});
/** marketplace-confirmDisputeCover (vendedor): al volver de Stripe, apunta el pago de la diferencia. */
export const ConfirmDisputeCoverInputSchema = z.object({
  session_id: z.string().min(1).max(255),
});
export interface ConfirmDisputeCoverOutput {
  paid: boolean;
  dispute_status: string;
}
export const DisputeReturnInputSchema = z.object({
  dispute_id: z.string().min(1).max(128),
  /** Comprador: tracking opcional al reenviar. */
  tracking_number: z.string().trim().max(80).optional(),
});
export interface RespondToDisputeOutput {
  dispute_status: string;
  status: OrderStatus;
  /** Parcial que la venta y el saldo no cubren: Stripe Checkout para pagar la diferencia. */
  payment_url?: string;
}

/** marketplace-respondToDisputeProposal (comprador): acepta o rechaza el reembolso parcial propuesto. */
export const RespondToDisputeProposalInputSchema = z.object({
  dispute_id: z.string().min(1).max(128),
  accept: z.boolean(),
});

/** marketplace-makeOffer: oferta del comprador en la moneda del anuncio (≥ 60 % del precio, < precio); caduca a las 24 h. */
export const MakeOfferInputSchema = z.object({
  product_id: z.string().min(1).max(128),
  amount: MoneySchema,
});
export interface MakeOfferOutput {
  offer_id: string;
  expires_at: number;
}

/**
 * marketplace-respondToOffer: el VENDEDOR acepta, rechaza o contraoferta una oferta pendiente; el
 * COMPRADOR acepta o rechaza una contraoferta. Aceptar → el comprador paga desde el checkout con
 * `offer_id` y el pedido nace aceptado (7b).
 */
export const RespondToOfferInputSchema = z.object({
  offer_id: z.string().min(1).max(128),
  action: z.enum(['accept', 'reject', 'counter']),
  counter_amount: MoneySchema.optional(),
});
export interface RespondToOfferOutput {
  status: (typeof OFFER_STATUSES)[number];
  /** Id de la contraoferta creada (action = counter). */
  counter_offer_id: string | null;
}

/** marketplace-cancelOrder: el comprador cancela gratis mientras el vendedor no ha aceptado (D8). */
export const CancelOrderInputSchema = z.object({ order_id: z.string().min(1).max(128) });
export interface CancelOrderOutput {
  status: OrderStatus;
}

/** marketplace-reopenPayment: pedido en pending_payment cuya sesión de Stripe se cerró → otra sesión (reserva renovada). */
export const ReopenPaymentInputSchema = z.object({
  order_id: z.string().min(1).max(128),
  language: LanguageCodeSchema,
});
export interface ReopenPaymentOutput {
  checkout_url: string;
  expires_at: number;
}

export const api = {
  admin: {
    /** Devuelve entorno, versión y resultado del chequeo de arranque. Patrón de referencia. */
    health: defineCall<typeof HealthInputSchema, HealthOutput>(
      'admin',
      'health',
      HealthInputSchema,
      { auth: 'none' },
    ),
    registerDevice: defineCall<typeof AdminDeviceInputSchema, AdminDeviceOutput>(
      'admin',
      'registerDevice',
      AdminDeviceInputSchema,
      { auth: 'required', admin: true },
    ),
    unregisterDevice: defineCall<typeof AdminDeviceInputSchema, AdminDeviceOutput>(
      'admin',
      'unregisterDevice',
      AdminDeviceInputSchema,
      { auth: 'required' },
    ),
    testAlert: defineCall<typeof AdminTestAlertInputSchema, AdminTestAlertOutput>(
      'admin',
      'testAlert',
      AdminTestAlertInputSchema,
      { auth: 'required', admin: true },
    ),
    grantRole: defineCall<typeof GrantRoleInputSchema, GrantRoleOutput>(
      'admin',
      'grantRole',
      GrantRoleInputSchema,
      { auth: 'required', admin: true },
    ),
    reviewWithdrawal: defineCall<typeof ReviewWithdrawalInputSchema, ReviewWithdrawalOutput>(
      'admin',
      'reviewWithdrawal',
      ReviewWithdrawalInputSchema,
      { auth: 'required', admin: true },
    ),
    updateSettings: defineCall<typeof UpdateSettingsInputSchema, UpdateSettingsOutput>(
      'admin',
      'updateSettings',
      UpdateSettingsInputSchema,
      { auth: 'required', admin: true },
    ),
    searchUsers: defineCall<typeof AdminSearchUsersInputSchema, AdminSearchUsersOutput>(
      'admin',
      'searchUsers',
      AdminSearchUsersInputSchema,
      { auth: 'required', admin: true },
    ),
    updateUser: defineCall<typeof AdminUpdateUserInputSchema, AdminUpdateUserOutput>(
      'admin',
      'updateUser',
      AdminUpdateUserInputSchema,
      { auth: 'required', admin: true },
    ),
    updateCarrier: defineCall<typeof AdminUpdateCarrierInputSchema, AdminOkOutput>(
      'admin',
      'updateCarrier',
      AdminUpdateCarrierInputSchema,
      { auth: 'required', admin: true },
    ),
    metrics: defineCall<typeof AdminRangeInputSchema, AdminMetricsOutput>(
      'admin',
      'metrics',
      AdminRangeInputSchema,
      {
        auth: 'required',
        admin: true,
      },
    ),
    moderateListing: defineCall<typeof AdminModerateListingInputSchema, AdminOkOutput>(
      'admin',
      'moderateListing',
      AdminModerateListingInputSchema,
      { auth: 'required', admin: true },
    ),
    orderAction: defineCall<typeof AdminOrderActionInputSchema, AdminOkOutput>(
      'admin',
      'orderAction',
      AdminOrderActionInputSchema,
      { auth: 'required', admin: true },
    ),
    notifyTermsUpdated: defineCall<
      typeof AdminNotifyTermsUpdatedInputSchema,
      AdminNotifyTermsUpdatedOutput
    >('admin', 'notifyTermsUpdated', AdminNotifyTermsUpdatedInputSchema, {
      auth: 'required',
      admin: true,
    }),
    dashboard: defineCall<typeof AdminDashboardInputSchema, AdminDashboardOutput>(
      'admin',
      'dashboard',
      AdminDashboardInputSchema,
      { auth: 'required', admin: true },
    ),
    revenueReport: defineCall<typeof AdminRevenueReportInputSchema, AdminRevenueReportOutput>(
      'admin',
      'revenueReport',
      AdminRevenueReportInputSchema,
      { auth: 'required', admin: true },
    ),
    updateShippingCampaign: defineCall<
      typeof AdminUpdateShippingCampaignInputSchema,
      AdminUpdateShippingCampaignOutput
    >('admin', 'updateShippingCampaign', AdminUpdateShippingCampaignInputSchema, {
      auth: 'required',
      admin: true,
    }),
    startImpersonation: defineCall<typeof StartImpersonationInputSchema, StartImpersonationOutput>(
      'admin',
      'startImpersonation',
      StartImpersonationInputSchema,
      { auth: 'required', admin: true },
    ),
    enrichListing: defineCall<typeof AdminEnrichListingInputSchema, AdminEnrichListingOutput>(
      'admin',
      'enrichListing',
      AdminEnrichListingInputSchema,
      { auth: 'required', admin: true },
    ),
    diagnostics: defineCall<typeof AdminDiagnosticsInputSchema, AdminDiagnosticsOutput>(
      'admin',
      'diagnostics',
      AdminDiagnosticsInputSchema,
      { auth: 'required', admin: true },
    ),
    saveSellerSurvey: defineCall<
      typeof AdminSaveSellerSurveyInputSchema,
      AdminSaveSellerSurveyOutput
    >('admin', 'saveSellerSurvey', AdminSaveSellerSurveyInputSchema, {
      auth: 'required',
      admin: true,
    }),
    sellerSurveys: defineCall<typeof AdminSellerSurveysInputSchema, AdminSellerSurveysOutput>(
      'admin',
      'sellerSurveys',
      AdminSellerSurveysInputSchema,
      { auth: 'required', admin: true },
    ),
    deleteSellerSurveyResponse: defineCall<
      typeof AdminDeleteSellerSurveyResponseInputSchema,
      AdminOkOutput
    >('admin', 'deleteSellerSurveyResponse', AdminDeleteSellerSurveyResponseInputSchema, {
      auth: 'required',
      admin: true,
    }),
    sendUserEmail: defineCall<typeof AdminSendUserEmailInputSchema, AdminSendUserEmailOutput>(
      'admin',
      'sendUserEmail',
      AdminSendUserEmailInputSchema,
      { auth: 'required', admin: true },
    ),
    sendBulkEmail: defineCall<typeof AdminSendBulkEmailInputSchema, AdminSendBulkEmailOutput>(
      'admin',
      'sendBulkEmail',
      AdminSendBulkEmailInputSchema,
      { auth: 'required', admin: true },
    ),
    systemInfo: defineCall<typeof EmptyInputSchema, SystemInfoOutput>(
      'admin',
      'systemInfo',
      EmptyInputSchema,
      { auth: 'required', admin: true },
    ),
    refreshFxRates: defineCall<typeof EmptyInputSchema, RefreshFxRatesOutput>(
      'admin',
      'refreshFxRates',
      EmptyInputSchema,
      { auth: 'required', admin: true },
    ),
    visitorStats: defineCall<typeof AdminRangeInputSchema, VisitorStatsOutput>(
      'admin',
      'visitorStats',
      AdminRangeInputSchema,
      { auth: 'required', admin: true },
    ),
    moderateReview: defineCall<typeof AdminModerateReviewInputSchema, AdminOkOutput>(
      'admin',
      'moderateReview',
      AdminModerateReviewInputSchema,
      { auth: 'required', admin: true },
    ),
    saveAdBanner: defineCall<typeof AdminSaveAdBannerInputSchema, AdminSaveAdBannerOutput>(
      'admin',
      'saveAdBanner',
      AdminSaveAdBannerInputSchema,
      { auth: 'required', admin: true },
    ),
    deleteAdBanner: defineCall<typeof AdminDeleteAdBannerInputSchema, AdminOkOutput>(
      'admin',
      'deleteAdBanner',
      AdminDeleteAdBannerInputSchema,
      { auth: 'required', admin: true },
    ),
    closeReport: defineCall<typeof AdminCloseReportInputSchema, AdminOkOutput>(
      'admin',
      'closeReport',
      AdminCloseReportInputSchema,
      { auth: 'required', admin: true },
    ),
  },
  marketplace: {
    adEvent: defineCall<typeof AdEventInputSchema, AdminOkOutput>(
      'marketplace',
      'adEvent',
      AdEventInputSchema,
      { auth: 'none' },
    ),
    featuredFeed: defineCall<typeof FeaturedFeedInputSchema, FeaturedFeedOutput>(
      'marketplace',
      'featuredFeed',
      FeaturedFeedInputSchema,
      { auth: 'none' },
    ),
    quoteCheckout: defineCall<typeof CheckoutQuoteInputSchema, CheckoutQuoteOutput>(
      'marketplace',
      'quoteCheckout',
      CheckoutQuoteInputSchema,
      { auth: 'required' },
    ),
    placeOrder: defineCall<typeof PlaceOrderInputSchema, PlaceOrderOutput>(
      'marketplace',
      'placeOrder',
      PlaceOrderInputSchema,
      { auth: 'required', viaHosting: true },
    ),
    reopenPayment: defineCall<typeof ReopenPaymentInputSchema, ReopenPaymentOutput>(
      'marketplace',
      'reopenPayment',
      ReopenPaymentInputSchema,
      { auth: 'required' },
    ),
    cancelOrder: defineCall<typeof CancelOrderInputSchema, CancelOrderOutput>(
      'marketplace',
      'cancelOrder',
      CancelOrderInputSchema,
      { auth: 'required' },
    ),
    respondToOrder: defineCall<typeof RespondToOrderInputSchema, RespondToOrderOutput>(
      'marketplace',
      'respondToOrder',
      RespondToOrderInputSchema,
      { auth: 'required' },
    ),
    confirmDelivery: defineCall<typeof ConfirmDeliveryInputSchema, ConfirmDeliveryOutput>(
      'marketplace',
      'confirmDelivery',
      ConfirmDeliveryInputSchema,
      { auth: 'required' },
    ),
    confirmHandover: defineCall<typeof ConfirmHandoverInputSchema, ConfirmHandoverOutput>(
      'marketplace',
      'confirmHandover',
      ConfirmHandoverInputSchema,
      { auth: 'required' },
    ),
    sendWithdrawalNotice: defineCall<
      typeof SendWithdrawalNoticeInputSchema,
      SendWithdrawalNoticeOutput
    >('marketplace', 'sendWithdrawalNotice', SendWithdrawalNoticeInputSchema, { auth: 'required' }),
    openDispute: defineCall<typeof OpenDisputeInputSchema, OpenDisputeOutput>(
      'marketplace',
      'openDispute',
      OpenDisputeInputSchema,
      { auth: 'required' },
    ),
    submitReview: defineCall<typeof SubmitReviewInputSchema, SubmitReviewOutput>(
      'marketplace',
      'submitReview',
      SubmitReviewInputSchema,
      { auth: 'required' },
    ),
    markReturnShipped: defineCall<typeof DisputeReturnInputSchema, RespondToDisputeOutput>(
      'marketplace',
      'markReturnShipped',
      DisputeReturnInputSchema,
      { auth: 'required' },
    ),
    confirmReturnReceived: defineCall<typeof DisputeReturnInputSchema, RespondToDisputeOutput>(
      'marketplace',
      'confirmReturnReceived',
      DisputeReturnInputSchema,
      { auth: 'required' },
    ),
    respondToDispute: defineCall<typeof RespondToDisputeInputSchema, RespondToDisputeOutput>(
      'marketplace',
      'respondToDispute',
      RespondToDisputeInputSchema,
      { auth: 'required' },
    ),
    respondToDisputeProposal: defineCall<
      typeof RespondToDisputeProposalInputSchema,
      RespondToDisputeOutput
    >('marketplace', 'respondToDisputeProposal', RespondToDisputeProposalInputSchema, {
      auth: 'required',
    }),
    confirmDisputeCover: defineCall<
      typeof ConfirmDisputeCoverInputSchema,
      ConfirmDisputeCoverOutput
    >('marketplace', 'confirmDisputeCover', ConfirmDisputeCoverInputSchema, { auth: 'required' }),
    resolveDispute: defineCall<typeof ResolveDisputeInputSchema, ResolveDisputeOutput>(
      'marketplace',
      'resolveDispute',
      ResolveDisputeInputSchema,
      { auth: 'required', admin: true },
    ),
    makeOffer: defineCall<typeof MakeOfferInputSchema, MakeOfferOutput>(
      'marketplace',
      'makeOffer',
      MakeOfferInputSchema,
      { auth: 'required' },
    ),
    respondToOffer: defineCall<typeof RespondToOfferInputSchema, RespondToOfferOutput>(
      'marketplace',
      'respondToOffer',
      RespondToOfferInputSchema,
      { auth: 'required' },
    ),
  },
  payments: {
    confirmOrderPayment: defineCall<
      typeof ConfirmOrderPaymentInputSchema,
      ConfirmOrderPaymentOutput
    >('payments', 'confirmOrderPayment', ConfirmOrderPaymentInputSchema, { auth: 'required' }),
    connectOnboarding: defineCall<typeof ConnectOnboardingInputSchema, ConnectOnboardingOutput>(
      'payments',
      'connectOnboarding',
      ConnectOnboardingInputSchema,
      { auth: 'required' },
    ),
    quoteWithdrawal: defineCall<typeof WithdrawalQuoteInputSchema, WithdrawalQuoteOutput>(
      'payments',
      'quoteWithdrawal',
      WithdrawalQuoteInputSchema,
      { auth: 'required' },
    ),
    requestWithdrawal: defineCall<typeof RequestWithdrawalInputSchema, RequestWithdrawalOutput>(
      'payments',
      'requestWithdrawal',
      RequestWithdrawalInputSchema,
      { auth: 'required' },
    ),
    createTopupSession: defineCall<typeof CreateTopupSessionInputSchema, CreateTopupSessionOutput>(
      'payments',
      'createTopupSession',
      CreateTopupSessionInputSchema,
      { auth: 'required' },
    ),
    confirmTopup: defineCall<typeof ConfirmTopupInputSchema, ConfirmTopupOutput>(
      'payments',
      'confirmTopup',
      ConfirmTopupInputSchema,
      { auth: 'required' },
    ),
    createProSession: defineCall<typeof CreateProSessionInputSchema, CreateProSessionOutput>(
      'payments',
      'createProSession',
      CreateProSessionInputSchema,
      { auth: 'required' },
    ),
    iapAppStore: defineCall<typeof IapAppStoreInputSchema, IapOutput>(
      'payments',
      'iapAppStore',
      IapAppStoreInputSchema,
      { auth: 'required' },
    ),
    iapPlay: defineCall<typeof IapPlayInputSchema, IapOutput>(
      'payments',
      'iapPlay',
      IapPlayInputSchema,
      { auth: 'required' },
    ),
    confirmPro: defineCall<typeof ConfirmProInputSchema, ProStatusOutput>(
      'payments',
      'confirmPro',
      ConfirmProInputSchema,
      { auth: 'required' },
    ),
    changeProPlan: defineCall<typeof ChangeProPlanInputSchema, ProStatusOutput>(
      'payments',
      'changeProPlan',
      ChangeProPlanInputSchema,
      { auth: 'required' },
    ),
    cancelPro: defineCall<typeof ProCancelInputSchema, ProStatusOutput>(
      'payments',
      'cancelPro',
      ProCancelInputSchema,
      { auth: 'required' },
    ),
    listCards: defineCall<typeof EmptyInputSchema, ListCardsOutput>(
      'payments',
      'listCards',
      EmptyInputSchema,
      { auth: 'required' },
    ),
    removeCard: defineCall<typeof RemoveCardInputSchema, AdminOkOutput>(
      'payments',
      'removeCard',
      RemoveCardInputSchema,
      { auth: 'required' },
    ),
    billingPortal: defineCall<typeof BillingPortalInputSchema, BillingPortalOutput>(
      'payments',
      'billingPortal',
      BillingPortalInputSchema,
      { auth: 'required' },
    ),
    purchaseMonetization: defineCall<
      typeof PurchaseMonetizationInputSchema,
      PurchaseMonetizationOutput
    >('payments', 'purchaseMonetization', PurchaseMonetizationInputSchema, { auth: 'required' }),
    confirmMonetization: defineCall<
      typeof ConfirmMonetizationInputSchema,
      ConfirmMonetizationOutput
    >('payments', 'confirmMonetization', ConfirmMonetizationInputSchema, { auth: 'required' }),
    activateFreeSpotlight: defineCall<
      typeof ActivateFreeSpotlightInputSchema,
      ActivateFreeSpotlightOutput
    >('payments', 'activateFreeSpotlight', ActivateFreeSpotlightInputSchema, { auth: 'required' }),
  },
  shipping: {
    markShipped: defineCall<typeof MarkShippedInputSchema, MarkShippedOutput>(
      'shipping',
      'markShipped',
      MarkShippedInputSchema,
      { auth: 'required' },
    ),
    listPickupPoints: defineCall<typeof ListPickupPointsInputSchema, ListPickupPointsOutput>(
      'shipping',
      'listPickupPoints',
      ListPickupPointsInputSchema,
      { auth: 'required' },
    ),
  },
  listings: {
    create: defineCall<typeof CreateListingInputSchema, CreateListingOutput>(
      'listings',
      'create',
      CreateListingInputSchema,
      { auth: 'required' },
    ),
    update: defineCall<typeof UpdateListingInputSchema, CreateListingOutput>(
      'listings',
      'update',
      UpdateListingInputSchema,
      { auth: 'required' },
    ),
    setStatus: defineCall<typeof SetListingStatusInputSchema, SetListingStatusOutput>(
      'listings',
      'setStatus',
      SetListingStatusInputSchema,
      { auth: 'required' },
    ),
    setAutopilot: defineCall<typeof SetAutopilotInputSchema, SetAutopilotOutput>(
      'listings',
      'setAutopilot',
      SetAutopilotInputSchema,
      { auth: 'required' },
    ),
    editPhoto: defineCall<typeof EditPhotoInputSchema, PhotoOutput>(
      'listings',
      'editPhoto',
      EditPhotoInputSchema,
      { auth: 'required' },
    ),
    photoBackground: defineCall<typeof PhotoUrlInputSchema, PhotoOutput>(
      'listings',
      'photoBackground',
      PhotoUrlInputSchema,
      { auth: 'required' },
    ),
    enhancePhoto: defineCall<typeof PhotoUrlInputSchema, PhotoOutput>(
      'listings',
      'enhancePhoto',
      PhotoUrlInputSchema,
      { auth: 'required' },
    ),
    aiStudio: defineCall<typeof AiStudioInputSchema, PhotoOutput>(
      'listings',
      'aiStudio',
      AiStudioInputSchema,
      { auth: 'required' },
    ),
    photoTools: defineCall<typeof EmptyInputSchema, PhotoToolsOutput>(
      'listings',
      'photoTools',
      EmptyInputSchema,
      { auth: 'required' },
    ),
    sellerStats: defineCall<typeof SellerStatsInputSchema, SellerStatsOutput>(
      'listings',
      'sellerStats',
      SellerStatsInputSchema,
      { auth: 'required' },
    ),
    recordProductView: defineCall<typeof RecordProductViewInputSchema, RecordProductViewOutput>(
      'listings',
      'recordProductView',
      RecordProductViewInputSchema,
      { auth: 'none' },
    ),
  },
  import: {
    extract: defineCall<typeof ImportExtractInputSchema, ImportExtractOutput>(
      'import',
      'extract',
      ImportExtractInputSchema,
      { auth: 'required' },
    ),
    extractPages: defineCall<typeof ImportExtractPagesInputSchema, ImportExtractOutput>(
      'import',
      'extractPages',
      ImportExtractPagesInputSchema,
      { auth: 'required' },
    ),
    publish: defineCall<typeof ImportPublishInputSchema, ImportPublishOutput>(
      'import',
      'publish',
      ImportPublishInputSchema,
      { auth: 'required' },
    ),
    linkReputation: defineCall<typeof LinkReputationInputSchema, LinkReputationOutput>(
      'import',
      'linkReputation',
      LinkReputationInputSchema,
      { auth: 'required' },
    ),
    unlinkReputation: defineCall<typeof UnlinkReputationInputSchema, { ok: true }>(
      'import',
      'unlinkReputation',
      UnlinkReputationInputSchema,
      { auth: 'required' },
    ),
  },
  ai: {
    disputeAssistant: defineCall<typeof DisputeAssistantInputSchema, DisputeAssistantOutput>(
      'ai',
      'disputeAssistant',
      DisputeAssistantInputSchema,
      { auth: 'required', admin: true },
    ),
    listingFromPhotos: defineCall<typeof ListingFromPhotosInputSchema, ListingFromPhotosOutput>(
      'ai',
      'listingFromPhotos',
      ListingFromPhotosInputSchema,
      { auth: 'required' },
    ),
    translateListing: defineCall<typeof TranslateListingInputSchema, TranslateListingOutput>(
      'ai',
      'translateListing',
      TranslateListingInputSchema,
      { auth: 'none' },
    ),
    translateQuery: defineCall<typeof TranslateQueryInputSchema, TranslateQueryOutput>(
      'ai',
      'translateQuery',
      TranslateQueryInputSchema,
      { auth: 'none' },
    ),
  },
  users: {
    endImpersonation: defineCall<typeof EmptyInputSchema, AdminOkOutput>(
      'users',
      'endImpersonation',
      EmptyInputSchema,
      { auth: 'required' },
    ),
    inviteFriend: defineCall<typeof InviteFriendInputSchema, InviteFriendOutput>(
      'users',
      'inviteFriend',
      InviteFriendInputSchema,
      { auth: 'required' },
    ),
    referralStatus: defineCall<typeof HealthInputSchema, ReferralStatusOutput>(
      'users',
      'referralStatus',
      HealthInputSchema,
      { auth: 'required' },
    ),
    signupGuard: defineCall<typeof SignupGuardInputSchema, SignupGuardOutput>(
      'users',
      'signupGuard',
      SignupGuardInputSchema,
      { auth: 'required', viaHosting: true },
    ),
    onSignup: defineCall<typeof OnSignupInputSchema, OnSignupOutput>(
      'users',
      'onSignup',
      OnSignupInputSchema,
      { auth: 'required', viaHosting: true },
    ),
    setUsername: defineCall<typeof SetUsernameInputSchema, SetUsernameOutput>(
      'users',
      'setUsername',
      SetUsernameInputSchema,
      { auth: 'required' },
    ),
    saveTaxProfile: defineCall<typeof SaveTaxProfileInputSchema, AdminOkOutput>(
      'users',
      'saveTaxProfile',
      SaveTaxProfileInputSchema,
      { auth: 'required' },
    ),
    taxProfileStatus: defineCall<typeof EmptyInputSchema, TaxProfileStatusOutput>(
      'users',
      'taxProfileStatus',
      EmptyInputSchema,
      { auth: 'required' },
    ),
    requestDataExport: defineCall<typeof EmptyInputSchema, DataExportOutput>(
      'users',
      'requestDataExport',
      EmptyInputSchema,
      { auth: 'required' },
    ),
    requestAccountDeletion: defineCall<
      typeof RequestAccountDeletionInputSchema,
      AccountDeletionOutput
    >('users', 'requestAccountDeletion', RequestAccountDeletionInputSchema, { auth: 'required' }),
    cancelAccountDeletion: defineCall<typeof EmptyInputSchema, AdminOkOutput>(
      'users',
      'cancelAccountDeletion',
      EmptyInputSchema,
      { auth: 'required' },
    ),
    setSellerType: defineCall<typeof SetSellerTypeInputSchema, AdminOkOutput>(
      'users',
      'setSellerType',
      SetSellerTypeInputSchema,
      { auth: 'required' },
    ),
    saveTraderProfile: defineCall<typeof SaveTraderProfileInputSchema, AdminOkOutput>(
      'users',
      'saveTraderProfile',
      SaveTraderProfileInputSchema,
      { auth: 'required' },
    ),
    getPublicProfile: defineCall<typeof PublicProfileInputSchema, PublicProfileOutput>(
      'users',
      'getPublicProfile',
      PublicProfileInputSchema,
      { auth: 'none' },
    ),
    waitlist: defineCall<typeof WaitlistInputSchema, WaitlistOutput>(
      'users',
      'waitlist',
      WaitlistInputSchema,
      { auth: 'none', viaHosting: true },
    ),
    saveStorefront: defineCall<typeof SaveStorefrontInputSchema, AdminOkOutput>(
      'users',
      'saveStorefront',
      SaveStorefrontInputSchema,
      { auth: 'required' },
    ),
    searchSellers: defineCall<typeof SearchSellersInputSchema, SearchSellersOutput>(
      'users',
      'searchSellers',
      SearchSellersInputSchema,
      { auth: 'none' },
    ),
    proSellers: defineCall<typeof EmptyInputSchema, ProSellersOutput>(
      'users',
      'proSellers',
      EmptyInputSchema,
      { auth: 'none' },
    ),
    reportDiagnostic: defineCall<typeof ReportDiagnosticInputSchema, AdminOkOutput>(
      'users',
      'reportDiagnostic',
      ReportDiagnosticInputSchema,
      { auth: 'optional' },
    ),
    sellerSurveyPrompt: defineCall<typeof EmptyInputSchema, SellerSurveyPromptOutput>(
      'users',
      'sellerSurveyPrompt',
      EmptyInputSchema,
      { auth: 'required' },
    ),
    submitSellerSurvey: defineCall<typeof SubmitSellerSurveyInputSchema, AdminOkOutput>(
      'users',
      'submitSellerSurvey',
      SubmitSellerSurveyInputSchema,
      { auth: 'required' },
    ),
    recordVisit: defineCall<typeof RecordVisitInputSchema, AdminOkOutput>(
      'users',
      'recordVisit',
      RecordVisitInputSchema,
      { auth: 'optional', viaHosting: true },
    ),
  },
  messaging: {
    markRead: defineCall<typeof ConversationRefInputSchema, MarkReadOutput>(
      'messaging',
      'markRead',
      ConversationRefInputSchema,
      { auth: 'required' },
    ),
    report: defineCall<typeof ReportConversationInputSchema, ReportConversationOutput>(
      'messaging',
      'report',
      ReportConversationInputSchema,
      { auth: 'required' },
    ),
  },
  notify: {
    registerPushToken: defineCall<typeof RegisterPushTokenInputSchema, RegisterPushTokenOutput>(
      'notify',
      'registerPushToken',
      RegisterPushTokenInputSchema,
      { auth: 'required' },
    ),
    unregisterPushToken: defineCall<
      typeof UnregisterPushTokenInputSchema,
      UnregisterPushTokenOutput
    >('notify', 'unregisterPushToken', UnregisterPushTokenInputSchema, { auth: 'required' }),
  },
  support: {
    createTicket: defineCall<typeof CreateTicketInputSchema, CreateTicketOutput>(
      'support',
      'createTicket',
      CreateTicketInputSchema,
      { auth: 'required' },
    ),
    replyTicket: defineCall<typeof ReplyTicketInputSchema, ReplyTicketOutput>(
      'support',
      'replyTicket',
      ReplyTicketInputSchema,
      { auth: 'required' },
    ),
    requestHuman: defineCall<typeof TicketIdInputSchema, AdminOkOutput>(
      'support',
      'requestHuman',
      TicketIdInputSchema,
      { auth: 'required' },
    ),
    closeTicket: defineCall<typeof TicketIdInputSchema, AdminOkOutput>(
      'support',
      'closeTicket',
      TicketIdInputSchema,
      { auth: 'required' },
    ),
    agentReply: defineCall<typeof AgentReplyInputSchema, AdminOkOutput>(
      'support',
      'agentReply',
      AgentReplyInputSchema,
      { auth: 'required', admin: true },
    ),
  },
  social: {
    previewNewsletter: defineCall<typeof EmptyInputSchema, NewsletterPreviewOutput>(
      'social',
      'previewNewsletter',
      EmptyInputSchema,
      { auth: 'required', admin: true },
    ),
    sendNewsletter: defineCall<typeof NewsletterTextsInputSchema, NewsletterSendOutput>(
      'social',
      'sendNewsletter',
      NewsletterTextsInputSchema,
      { auth: 'required', admin: true },
    ),
    socialPublishNow: defineCall<typeof SocialPublishNowInputSchema, SocialPublishNowOutput>(
      'social',
      'socialPublishNow',
      SocialPublishNowInputSchema,
      { auth: 'required', admin: true },
    ),
    socialTestConnection: defineCall<typeof EmptyInputSchema, SocialTestConnectionOutput>(
      'social',
      'socialTestConnection',
      EmptyInputSchema,
      { auth: 'required', admin: true },
    ),
    saveNewsletterSchedule: defineCall<typeof NewsletterScheduleInputSchema, AdminOkOutput>(
      'social',
      'saveNewsletterSchedule',
      NewsletterScheduleInputSchema,
      { auth: 'required', admin: true },
    ),
    resetNewsletterHistory: defineCall<typeof EmptyInputSchema, AdminOkOutput>(
      'social',
      'resetNewsletterHistory',
      EmptyInputSchema,
      { auth: 'required', admin: true },
    ),
  },
  forum: {
    createThread: defineCall<typeof ForumCreateThreadInputSchema, ForumCreatedOutput>(
      'forum',
      'createThread',
      ForumCreateThreadInputSchema,
      { auth: 'required' },
    ),
    createPost: defineCall<typeof ForumCreatePostInputSchema, ForumCreatedOutput>(
      'forum',
      'createPost',
      ForumCreatePostInputSchema,
      { auth: 'required' },
    ),
    moderate: defineCall<typeof ForumModerateInputSchema, AdminOkOutput>(
      'forum',
      'moderate',
      ForumModerateInputSchema,
      { auth: 'required' },
    ),
    translate: defineCall<typeof ForumTranslateInputSchema, ForumTranslateOutput>(
      'forum',
      'translate',
      ForumTranslateInputSchema,
      { auth: 'none' },
    ),
    seed: defineCall<typeof EmptyInputSchema, ForumSeedOutput>('forum', 'seed', EmptyInputSchema, {
      auth: 'required',
      admin: true,
    }),
  },
  compliance: {
    reportContent: defineCall<typeof ReportContentInputSchema, ReportContentOutput>(
      'compliance',
      'reportContent',
      ReportContentInputSchema,
      { auth: 'required' },
    ),
    dsaNotice: defineCall<typeof DsaNoticeInputSchema, DsaNoticeOutput>(
      'compliance',
      'dsaNotice',
      DsaNoticeInputSchema,
      { auth: 'none' },
    ),
    requestModerationReview: defineCall<typeof RequestModerationReviewInputSchema, AdminOkOutput>(
      'compliance',
      'requestModerationReview',
      RequestModerationReviewInputSchema,
      { auth: 'required' },
    ),
    resolveCase: defineCall<typeof AdminResolveComplianceCaseInputSchema, AdminOkOutput>(
      'compliance',
      'resolveCase',
      AdminResolveComplianceCaseInputSchema,
      { auth: 'required', admin: true },
    ),
    createCase: defineCall<typeof AdminCreateComplianceCaseInputSchema, AdminOkOutput>(
      'compliance',
      'createCase',
      AdminCreateComplianceCaseInputSchema,
      { auth: 'required', admin: true },
    ),
    dac7Summary: defineCall<typeof AdminDac7SummaryInputSchema, Dac7SummaryOutput>(
      'compliance',
      'dac7Summary',
      AdminDac7SummaryInputSchema,
      { auth: 'required', admin: true },
    ),
  },
} as const;

export type Api = typeof api;
